import React from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styles from './styles.less';
import * as PicabooConstants from '../../../../common/PicabooConstants';
import {
  activatePlacer,
  captionEditing,
  clearSelection,
  deleteSelectedPageItems,
  enableBackgroundPlacer,
  enableDesignPlacer,
  enableEyeDropper,
  enableLayoutPlacer,
  enableShapePlacer,
  enableSwapPhotoPicker,
  selectPageItems,
  selectPages,
  showPositionModal,
  textSelected,
} from '../../../../ducks/selection';
import {
  renderCanvasFromStore,
  setEditorDim,
  setIsResizing,
  setSelectedTab,
  TAB_EDIT_PHOTO,
  TAB_EDIT_RICH_TEXT,
  toggleImageManagement,
  setSidePanelOpened,
  togglePhotosModal,
  TAB_WARNINGS,
} from '../../../../ducks/navigation';
import { PageRenderer } from './PageRenderer';
import { GraphicItemUtils } from '../../../../utils/GraphicItemUtils';
import { GraphicItemMasks } from './GraphicItemMasks';
import { addProjectImage, updateWarnings } from '../../../../ducks/project';
import { showConfirmationModal } from '../../../../ducks/modals';
import {
  addPageItems,
  createGraphicItem,
  createLineItem,
  createRichTextItem,
  createShapeItem,
  createTextItem,
  updatePageItems,
  updatePageItemsNoUndo,
} from '../../../../ducks/pageItems';
import { BackgroundImageToPageItemAdapter } from '../../../../common/PageItem';
import { toHex } from '../../../../utils/color';
import { convertSpreadPages } from '../../../../ducks/pages';
import { BackgroundToProjectImageAdapter } from '../../../../common/ProjectImage';
import {
  convertBackground,
  setBackgroundAllPages,
  setBackgroundColor,
  setBackgroundImage,
} from '../../../../ducks/backgrounds';
import * as Selectors from '../../../../reducers/selectors';
import ZoneOverlay from '../../../../components/ZoneOverlay';
import SnapOverlay from '../../../../components/SnapOverlay';
import WrapArea from '../../../../components/WrapArea';
import SelectionOverlay from '../../../../components/SelectionOverlay';
import { contains, getContainingRectangle } from '../../../../utils/geometry';
import { SnapLocations } from '../../../../helpers/SnapLocationHelper';
import logEvent from '../../../../utils/TagManager';
import SpineHelper from '../../../../helpers/SpineHelper';
import PageRendererEmptyWellHelper from './PageRendererEmptyWellHelper';
import PicabooModal from '../../../../components/PicabooModal';
import { Button, Dropdown, Modal } from 'react-bootstrap';
import {
  applyLayoutToPages,
  applyPageDesignToPages,
  applyPageLayoutToPages,
} from '../../../../ducks/layouts';
import { updatePages } from '../../../../ducks/changesets';
import * as PageUtils from '../../../../models/PageUtils';
import { fromJS, is, List } from 'immutable';
import FabricUtils from '../../../../utils/FabricUtils';
import { frameTextureImages } from '../../../../utils/frameUtils';
import { isMobile } from '../../../../components/PicabooResponsive';
import PicabooComponentBase from '../../../../components/PicabooComponentBase';
import CoordinateHelper from '../../../../helpers/CoordinateHelper';
import { isPURBook } from '../../../../utils/subtypeHelper';
import { fabric } from 'fabric';
import { PANNING } from './PageRendererHelperBase';
import { Point, Line, Segment, intersections } from '@mathigon/euclid';
import MaterialIcon from '../../../../components/MaterialIcon';
import fabricUtils from '../../../../utils/FabricUtils';

const KEY = {
  BACKSPACE: 8,
  DELETE: 46,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
};

export function InsideCoverOverlay({
  spreadWidth,
  productInfo,
  isPUR = false,
  frontInside = true,
  currentPage = null,
}) {
  const DPI = 300;
  const LOGO_HEIGHT_IN_INCHES = 0.5;
  const LOGO_PADDING_BOTTOM_IN_INCHES = 1.5;

  //Half Spread
  let screenWidth = spreadWidth / 2;
  let pw = productInfo.getIn(['resolutionFactor', 'printedWidth']);
  //Product Width
  let inches = pw / DPI;
  let pixel_per_inch = screenWidth / inches;
  let image_height = pixel_per_inch * LOGO_HEIGHT_IN_INCHES;
  let bottom = pixel_per_inch * LOGO_PADDING_BOTTOM_IN_INCHES;
  let left = screenWidth + screenWidth / 2 - image_height / 2;
  let currentPageNumber = currentPage.get('pageNumber')
    ? currentPage.get('pageNumber').get(0)
    : 0;
  let coverMessage =
    currentPageNumber < 1
      ? 'These pages cannot be edited'
      : 'This page cannot be edited';
  return (
    <React.Fragment>
      <div className={styles.insideCover}>
        <div>{coverMessage}</div>
      </div>
    </React.Fragment>
  );
}

/**
 * Calculates the image_positionX and image_positionY for a page item.
 * @param pageItem The page item metadata
 * @param group The fabric object representing the image
 * @param outline The fabric object representing the outline of the page item
 * @returns {{x: number, y: number}}
 */
function fabricPositionToPageItemPosition(pageItem, group, outline) {
  // The coordinates represent the four corners of the fabric object, clockwise starting from the top left
  const groupCoords = group.getCoords();
  const outlineCoords = outline.getCoords();

  // When a page item is rotated, calculating the image offset is more complex than just
  // subtracting the outline's top and left from the group's top and left. Rather, we need to
  // find where the edge of the outline would intersect with the group's edge. The distance
  // from the top left corner of the outline to the intersection point is the hypotenuse of a
  // right triangle, and represents the offset of the image we ultimately use to calculate
  // image_positionX and image_positionY.

  let imagePositionY = 0;
  const pageItemDiff = pageItem.get('image_height') - pageItem.get('height');

  if (pageItemDiff !== 0) {
    const groupTopEdge = new Line(
      new Point(groupCoords[0].x, groupCoords[0].y),
      new Point(groupCoords[1].x, groupCoords[1].y),
    );

    const outlineLeftEdge = new Line(
      new Point(outlineCoords[0].x, outlineCoords[0].y),
      new Point(outlineCoords[3].x, outlineCoords[3].y),
    );

    const intersectPoints = intersections(groupTopEdge, outlineLeftEdge);
    const intersectPoint = intersectPoints[0];

    const groupDiff = group.height - outline.height;
    const groupPageItemRatio = groupDiff / pageItemDiff;

    const topOffset = Math.sqrt(
      (intersectPoint.x - outline.left) ** 2 +
        (intersectPoint.y - outline.top) ** 2,
    );
    imagePositionY = topOffset / groupPageItemRatio;
  }

  let imagePositionX = 0;
  const pageItemDiffX = pageItem.get('image_width') - pageItem.get('width');
  if (pageItemDiffX !== 0) {
    const groupLeftEdge = new Line(
      new Point(groupCoords[0].x, groupCoords[0].y),
      new Point(groupCoords[3].x, groupCoords[3].y),
    );

    const outlineTopEdge = new Line(
      new Point(outlineCoords[0].x, outlineCoords[0].y),
      new Point(outlineCoords[1].x, outlineCoords[1].y),
    );

    const intersectPoints = intersections(groupLeftEdge, outlineTopEdge);
    const intersectPoint = intersectPoints[0];

    const groupDiffX = group.width - outline.width;
    const groupPageItemRatioX = groupDiffX / pageItemDiffX;

    const leftOffset = Math.sqrt(
      (intersectPoint.x - outline.left) ** 2 +
        (intersectPoint.y - outline.top) ** 2,
    );
    imagePositionX = leftOffset / groupPageItemRatioX;
  }

  return { x: -imagePositionX, y: -imagePositionY };
}

/**
 * Calculates the top and left position of the uncropped image in a page item.
 * @param pageItem The page item metadata
 * @param group The fabric object representing the image
 * @param outline The fabric object representing the outline of the page item
 * @returns {{top: *, left: number}}
 */
function pageItemPositionToFabric(pageItem, group, outline) {
  const radians = (group.angle * Math.PI) / 180.0;
  let imagePositionY = pageItem.get('image_positionY') ?? 0;
  let imagePositionX = pageItem.get('image_positionX') ?? 0;

  const x =
    imagePositionX * Math.cos(radians) - imagePositionY * Math.sin(radians);
  const y =
    imagePositionY * Math.cos(radians) + imagePositionX * Math.sin(radians);

  imagePositionX = -x;
  imagePositionY = y;

  const pageItemDiff = pageItem.get('image_height') - pageItem.get('height');
  const groupDiff = group.height - outline.height;
  const groupPageItemRatio =
    groupDiff / (pageItemDiff === 0 ? 1 : pageItemDiff);
  const topOffset = imagePositionY * groupPageItemRatio;
  const top = outline.top + topOffset;

  const pageItemDiffX = pageItem.get('image_width') - pageItem.get('width');
  const groupDiffX = group.width - outline.width;
  const groupPageItemRatioX =
    groupDiffX / (pageItemDiffX === 0 ? 1 : pageItemDiffX);
  const leftOffset = imagePositionX * groupPageItemRatioX;
  const left = outline.left - leftOffset;

  return { top, left };
}

/**
 * This function restricts the dragging of the image so that it always fills the outline (of the page item).
 * @param group The fabric group object that is being dragged (displays the image)
 * @param firstLeft The left position of the group when the drag started
 * @param firstTop The top position of the group when the drag started
 * @param startX The x position of the mouse when the drag started
 * @param startY The y position of the mouse when the drag started
 * @param pointerX The current x position of the mouse
 * @param pointerY The current y position of the mouse
 * @param outlineRotate The rectangle representing the area in which the upper left corner of the image can be placed
 * @param lines The lines representing the edges of the outlineRotate rectangle
 */
function handlePanning(
  group,
  firstLeft,
  firstTop,
  startX,
  startY,
  pointerX,
  pointerY,
  outlineRotate,
  lines,
) {
  group.left = firstLeft - (startX - pointerX);
  group.top = firstTop - (startY - pointerY);

  if (!outlineRotate.containsPoint(new fabric.Point(group.left, group.top))) {
    let distances = [];
    lines.forEach((line, i) => {
      let d = Math.sqrt(line.distanceSquared(new Point(group.left, group.top)));
      if (!line.contains(new Point(group.left, group.top))) {
        let result = {};
        result.d = d;
        result.line = line;
        result.index = i;

        distances.push(result);
      }
    });

    let movePoint = new Point(group.left, group.top);
    distances.sort((a, b) => a.d - b.d);

    if (distances[0].line) {
      let p = distances[0].line.project(movePoint);
      group.left = p.x;
      group.top = p.y;
    }
  }
}

class CanvasContainer extends PicabooComponentBase {
  constructor() {
    super();
    this.state = {
      currentEyeDropperEnabled: false,
      selectedEyeDropperColor: '#ffffff',
      validEyeDropper: false,
      showAddGraphicItemModal: false,
      anchorEl: null,
      droppedImage: null,
      dropEvent: null,
      showReplace: false,
      showAdd: false,
      showBackground: false,
      containerWidth: 0,
      containerHeight: 0,
      pageWidth: 0,
      pageHeight: 0,
      safetyVisible: false,
      showHorizontalSnap: false,
      showVerticalSnap: false,
      snapX: 0,
      snapY: 0,
      snapOffSetX: 0,
      snapOffSetY: 0,
      showWellMenu: false,
      wellMenuCoords: {},
      showConvertDialog: false,
      showConvertSpreadDialog: false,
      convertDialogItems: null,
      isPanning: false,
    };

    this.resizeTimeout = -1;
    this.safetyTimeout = -1;
    this.snapPoints = new SnapLocations();
    this.snappingX = false;
    this.snappingY = false;
    this.snapped = false;
    this.snapKeepTop = -1;
    this.snapKeepLeft = -1;
    this.renderCanvas = -1;
    this.renderPageID = null;
    this.activeResize = false;
    this.currentCorner = undefined;
  }

  shouldComponentUpdate(nextProps, nextState) {
    let retval =
      !nextProps.project.equals(this.props.project) ||
      (nextProps.page && !this.pageIsEqual(nextProps.page, this.props.page)) ||
      !this.pagesAreEqual(nextProps.pages, this.props.pages) ||
      !nextProps.wrap.equals(this.props.wrap) ||
      !this.pagesAreEqual(nextProps.selectedPages, this.props.selectedPages) ||
      // || !nextProps.pageItems.equals(this.props.pageItems)
      nextState.showAddGraphicItemModal != this.state.showAddGraphicItemModal ||
      nextState.pageWidth != this.state.pageWidth ||
      nextState.pageHeight != this.state.pageHeight ||
      nextState.containerHeight != this.state.containerHeight ||
      nextState.containerWidth != this.state.containerWidth ||
      nextState.showSafety != this.state.showSafety ||
      !nextProps.settings.equals(this.props.settings) ||
      nextProps.renderCanvas != this.props.renderCanvas ||
      // || nextProps.selection != this.props.selection
      nextState.showHorizontalSnap != this.state.showHorizontalSnap ||
      nextState.showVerticalSnap != this.state.showVerticalSnap ||
      nextState.snapX != this.state.snapX ||
      nextState.snapY != this.state.snapY ||
      nextState.showWellMenu != this.state.showWellMenu ||
      nextState.showConvertDialog !== this.state.showConvertDialog ||
      nextProps.previewMode !== this.props.previewMode ||
      nextProps.selection.placerEnabled !==
        this.props.selection.placerEnabled ||
      nextProps.selection.spellingIndex !==
        this.props.selection.spellingIndex ||
      nextProps.selection.shapeSpotterEnabled !==
        this.props.selection.shapeSpotterEnabled ||
      nextProps.selection.layoutPlacerEnabled !==
        this.props.selection.layoutPlacerEnabled ||
      nextState.isPanning !== this.state.isPanning ||
      nextProps.selection.eyeDropperEnabled !==
        this.props.selection.eyeDropperEnabled ||
      nextProps.reviewMode != this.props.reviewMode ||
      nextProps.sidePanelOpened !== this.props.sidePanelOpened;
    return retval;
  }

  componentDidMount() {
    this.renderer = null;
    //window.devicePixelRatio = 1;
    window['canvas' + this.props.uid] = this.canvas = new fabric.Canvas(
      'c' + this.props.uid,
      {
        preserveObjectStacking: true,
        stateful: true,
        enableRetinaScaling: true,
        uniScaleKey: 'none', //prevents user from holding shift key while resizing to change behavior (BWO-119)
      },
    );

    fabricUtils.setup();
    fabric.Object.prototype.snapAngle = 1;
    this.setupCanvasEvents(this.props);
    window.addEventListener('resize', this.handleResize);

    if (this.props.wrap.get('loaded')) {
      this.setPageRenderer(this.props);
    }
  }

  componentWillUnmount() {
    let canvasContainer = this.getElement('canvasDiv');
    if (canvasContainer) {
      canvasContainer.removeEventListener('drop', this.handleDrop);
    }
    if (this.canvas) {
      this.canvas.off();
    }
    if (window) {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  getElement = (id) => {
    return document.getElementById(id + this.props.uid);
  };

  showAddGraphicItemModal = (
    anchorEl,
    dropEvent,
    droppedImage,
    showReplace,
    showAdd,
    showBackground,
  ) => {
    this.setState({
      ...this.state,
      showAddGraphicItemModal: true,
      anchorEl,
      droppedImage,
      dropEvent,
      showReplace,
      showAdd,
      showBackground,
    });
  };

  closeAddGraphicItemModal = (reason = '') => {
    this.setState({ ...this.state, showAddGraphicItemModal: false });
  };

  handleResize = () => {
    let canvasContainer = this.getElement('canvasDiv');
    if (!canvasContainer || this.props.uid != 0) {
      return;
    }
    this.canvas.clear();
    canvasContainer.style.display = 'none';
    canvasContainer.style.visibility = 'hidden';

    let page = this.getPage();
    clearTimeout(this.resizeTimeout);
    this.props.dispatch(setIsResizing(true));

    if (page) {
      this.resizeTimeout = setTimeout(
        function () {
          this.props.dispatch(setIsResizing(false));
          this.props.dispatch(renderCanvasFromStore());
        }.bind(this),
        300,
      );
    }
  };

  componentDidUpdate(prevProps, prevState) {
    // change the mode of the canvas (interactive and event handling when the state of the eyeDropper changes.
    if (
      prevProps.selection.eyeDropperEnabled !==
        this.props.selection.eyeDropperEnabled ||
      prevProps.selection.shapeSpotterEnabled !==
        this.props.selection.shapeSpotterEnabled ||
      prevProps.selection.swapPhotoEnabled !==
        this.props.selection.swapPhotoEnabled ||
      prevProps.selection.backgroundPlacerEnabled !==
        this.props.selection.backgroundPlacerEnabled ||
      prevProps.selection.layoutPlacerEnabled !==
        this.props.selection.layoutPlacerEnabled ||
      prevProps.selection.designPlacerEnabled !==
        this.props.selection.designPlacerEnabled ||
      prevProps.selection.placerEnabled !==
        this.props.selection.placerEnabled ||
      prevState.isPanning !== this.state.isPanning
    ) {
      this.setupCanvasEvents(this.props);
    }

    if (
      prevProps.fonts.length == 0 &&
      this.props.fonts.hasOwnProperty('fonts') &&
      this.props.fonts.fonts.length > 0
    ) {
      if (this.renderCanvas > -1) {
        this.redrawCanvas();
      }
    }
    if (
      prevProps.previewMode !== this.props.previewMode ||
      this.props.sidePanelOpened !== prevProps.sidePanelOpened
    ) {
      if (!isMobile() || this.props.sidePanelOpened === false) {
        setTimeout(
          function () {
            this.handleResize();
            this.props.dispatch(renderCanvasFromStore());
          }.bind(this),
          10,
        );
      }
    }

    if (!prevProps.pageItems.equals(this.props.pageItems)) {
      let removedItems = prevProps.pageItems.filter(
        (pi) => this.props.pageItems.get(pi.get('id')) == undefined,
      );
      removedItems.map((pi) => this.removeSnapPoints(pi.get('id')));
    }

    let canvasContainer = this.getElement('canvasDiv');
    if (canvasContainer) {
      canvasContainer.style.pointerEvents =
        this.props.previewMode || this.props.reviewMode ? 'none' : 'all';
    }
    if (
      prevProps.previewMode !== this.props.previewMode ||
      this.props.reviewMode
    ) {
      if (this.canvas.getActiveObject() || this.canvas.getActiveObjects()) {
        this.props.dispatch(clearSelection());
        this.canvas.discardActiveObject().requestRenderAll();
      }
    }

    //the spine caption was drawing before the wrap was loaded, so
    //if the wrap is 0, the spine caption returns without drawing and we
    //redraw here
    if (!is(this.props.wrap, prevProps.wrap)) {
      if (this.renderer) {
        this.renderer.updateWrap(this.props.wrap);
      }
      // const page = this.getPage();
      // if (this.renderer && page && page.get('spineCaption')){
      //     this.renderer.redrawSpineCaption(page.get('spineCaption'))
      // }
    }

    if (this.props.wrap.get('loaded')) {
      this.setPageRenderer(this.props);
    }

    if (this.canvas) {
      this.canvas.selection = true;
    }

    // Fix for multiple captions on the same page having spelling errors
    if (
      this.props.selection.spellingIndex !== prevProps.selection.spellingIndex
    ) {
      this.maintainSelection();
    }
  }

  setPageRenderer(props) {
    let m = new Date().getMilliseconds();
    global.isDesigner = this.props.isDesigner;

    //Clear any selections if this isn't the selected page
    let canvas = this.canvas;

    if (!this.currentPageIsSelected()) {
      if (canvas.getActiveObject() || canvas.getActiveObjects()) {
        canvas.discardActiveObject().requestRenderAll();
      }
    }

    this.addDefaultCenterPoints();
    this.addPageSnapPoints();

    let page = this.getPage();
    if (page) {
      if (props.renderPageID && props.renderPageID != page.get('id')) {
        return;
      } else if (props.renderCanvas == this.renderCanvas) {
        return;
      }

      // Set interactivity based on loading state
      props.loadingProject ? this.makeNonInteractive() : this.makeInteractive();

      let interactive =
        props.previewMode || props.reviewMode || props.loadingProject
          ? false
          : !props.selection.eyeDropperEnabled;
      if (
        this.props.section &&
        props.section.get('type') === PicabooConstants.SECTION_TYPES.TYPE_INDEX
      ) {
        interactive = interactive && this.props.project.get('canEditIndex');
      }

      this.renderCanvas = props.renderCanvas;
      this.renderPageID = props.renderPageID;
      let canvasContainer = this.getElement('canvasDiv');
      canvasContainer.style.display = 'block';
      this.dim = this.calcPageDimensions();
      if (
        this.renderer &&
        this.dim.width != this.renderer.currentPageDimensions.width
      ) {
        canvas.clear();
      }

      let pageRenderID =
        (PageRenderer.getCurrentPageRenderID(page.get('id')) || 0) + 1;
      PageRenderer.setCurrentPageRenderID(page.get('id'), pageRenderID);
      let style = props.project.get('style');
      let pageID = props.page.get('id');

      let spreadID;
      props.section.get('spreads').map((spread) => {
        spread.get('pages').map((page) => {
          if (page.get('pageID') === pageID) {
            spreadID = spread.get('id');
          }
        });
      });

      this.renderer = new PageRenderer({
        canvas,
        pageDimensions: this.dim,
        interactive,
        pageRenderID,
        textSelectionChanged: this.handleTextSelectionChange,
        pageItemUpdated: this.pageItemUpdated,
        productInfo: props.productInfo,
        numPages: props.project.get('numPages'),
        style: style,
        spineCaption: page.get('spineCaption'),
        wrap: props.wrap,
        overlay: page.get('overlay'),
        stamping: page.get('coverStamping'),
        preferences: props.project.get('preferences'),
        captionVersion: props.project.get('captionVersion'),
        isMobile: isMobile(),
        projectPK: this.props.project.get('projectPK'),
        subType: props.project.get('subtype'),
        foilColor: props.project.get('cover'),
        projectWarnings: props.project.get('warnings'),
        handleUpdateWarnings: this.handleUpdateWarnings,
        pageSpreadID: spreadID,
        withWarnings: true,
      });

      this.renderer.clearSelection(); //otherwise errors happen if selected items have been deleted
      let pageItems = props.reviewMode
        ? props.reviewProject.get('pageItems')
        : props.pageItems;

      this.renderer.drawPage(
        page,
        pageItems,
        props.images,
        props.selection,
        function () {
          //Required for 'select all' functionality and for moving with arrow keys
          canvasContainer.style.visibility = 'visible';
          this.maintainSelection();
          if (!interactive) {
            this.canvas.requestRenderAll();
          }
        }.bind(this),
        !props.reviewMode,
      );

      this.drawSafetyZone();
    }
  }

  maintainSelection = () => {
    if (this.props.selection.spellingErrors) {
      if (this.props.selection.spellingIndex !== null) {
        const spellError =
          this.props.selection.spellingErrors[
            this.props.selection.spellingIndex
          ];
        if (spellError) {
          this.renderer.selectMulti([spellError.pageItemID]);
          this.props.dispatch(captionEditing(true));
          return;
        }
      }
    }

    if (this.currentPageIsSelected()) {
      if (
        this.props.selectedPageItems.size > 1 &&
        this.props.selectedPageItems.size ===
          this.getPage().get('pageItems').size
      ) {
        this.renderer.selectAll();
      } else if (this.props.selectedPageItems.size > 0) {
        this.renderer.selectMulti(
          this.props.selectedPageItems.toArray().map((a) => a.get('id')),
        );
      }
    }
  };

  addDefaultCenterPoints() {
    let page = this.getPage();
    if (page && this.dim) {
      let centerX = 0.5 * this.dim.width;
      let centerY = 0.5 * this.dim.height;
      this.snapPoints.setCenter(centerX, centerY);

      if (this.isCoverSection() && this.props.wrap.get('showSpine')) {
        this.removeSnapPoints('spreadLeft');
        this.removeSnapPoints('spreadRight');
        const pageWidth = this.state.pageWidth;
        const widthBase = Math.max(pageWidth, this.state.containerWidth);

        const leftPageEdge = (widthBase - pageWidth) / 2;

        let isSoftCover =
          this.props.section.get('type') ===
          PicabooConstants.SECTION_TYPES.TYPE_SOFTCOVER;
        let spineWidth = SpineHelper.calculateSpineWidth(
          pageWidth,
          this.props.project.get('numPages'),
          this.props.project.get('subtype'),
          this.props.project.get('style'),
          this.props.productInfo,
        );

        const leftSpine = (pageWidth - spineWidth) / 2;
        const outerAdjustment = isSoftCover ? 0 : spineWidth / 2; // i wish this was commented when it was originall y
        const horizontalWrap =
          this.props.wrap.get('horizontalWrap') * pageWidth +
          leftPageEdge -
          outerAdjustment;

        const workingPageWidth = leftSpine - horizontalWrap;

        const backCoverCenter =
          (leftSpine - horizontalWrap) / 2 + horizontalWrap;
        const frontCoverCenter =
          pageWidth - horizontalWrap - workingPageWidth / 2;
        this.snapPoints.setOther(backCoverCenter, null, 'coverLeft', 8);
        this.snapPoints.setOther(frontCoverCenter, null, 'coverRight', 8);
      } else {
        this.removeSnapPoints('coverLeft');
        this.removeSnapPoints('coverRight');
        if (this.props.isBook && !this.props.twoPage) {
          this.snapPoints.setOther(centerX / 2, null, 'spreadLeft', 8);
          this.snapPoints.setOther(
            centerX / 2 + centerX,
            null,
            'spreadRight',
            8,
          );
        }
      }
    }
  }

  addPageSnapPoints() {
    this.snapPoints.setOther(0, null, 'pageLeft', 8);
    this.snapPoints.setOther(null, 0, 'pageTop', 8);
    if (this.dim) {
      this.snapPoints.setOther(this.dim.width, null, 'pageRight', 8);
      this.snapPoints.setOther(null, this.dim.height, 'pageBottom', 8);
    }
  }

  addUpdateSnapPoints(pageItemID, fabricObj) {
    this.snapPoints.xLocations = this.snapPoints.xLocations.filter((loc) => {
      return loc.pageItemID !== pageItemID;
    });
    this.snapPoints.yLocations = this.snapPoints.yLocations.filter((loc) => {
      return loc.pageItemID !== pageItemID;
    });

    const SNAP_TOLERANCE = 5;
    let borderBuffer = 0;
    if (fabricObj != undefined) {
      if (!fabricObj.selectable) {
        return;
      }
      if (fabricObj.get('type') === 'group') {
        // getBoundingRect seems to only apply to the last object in a group.  for GraphicItems, this will exclude the border
        if (fabricObj.pageItemVO) {
          borderBuffer = this.getActualBorderFromItem(fabricObj.pageItemVO);
        }
      }

      fabricObj.setCoords();
      let boundRect = fabricObj.getBoundingRect();
      this.snapPoints.xLocations.push({
        pageItemID: pageItemID,
        loc: boundRect.left + fabricObj.padding - borderBuffer,
        showDecorator: true,
        tolerance: SNAP_TOLERANCE,
      });
      this.snapPoints.xLocations.push({
        pageItemID: pageItemID,
        loc:
          boundRect.left + boundRect.width - fabricObj.padding + borderBuffer,
        showDecorator: true,
        tolerance: SNAP_TOLERANCE,
      });
      this.snapPoints.yLocations.push({
        pageItemID: pageItemID,
        loc: boundRect.top + fabricObj.padding - borderBuffer,
        showDecorator: true,
        tolerance: SNAP_TOLERANCE,
      });
      this.snapPoints.yLocations.push({
        pageItemID: pageItemID,
        loc:
          boundRect.top + boundRect.height - fabricObj.padding + borderBuffer,
        showDecorator: true,
        tolerance: SNAP_TOLERANCE,
      });
    }
  }

  removeSnapPoints(pageItemID) {
    this.snapPoints.xLocations = this.snapPoints.xLocations.filter((loc) => {
      return loc.pageItemID !== pageItemID;
    });
    this.snapPoints.yLocations = this.snapPoints.yLocations.filter((loc) => {
      return loc.pageItemID !== pageItemID;
    });
  }

  drawSafetyZone() {
    //let canvasDiv = this.refs.canvasDiv;

    if (!this.dim) return;

    this.setState({
      pageWidth: this.dim.width,
      pageHeight: this.dim.height,
      containerWidth: this.getClientWidth(), // canvasDiv.clientWidth,
      // Aaron can't remember why we were doing the stuff down below but this works.  If we didn't do it, wrap
      // zones are all messed up
      containerHeight: this.dim.height,
    });
  }

  getClientWidth = () => {
    // Aaron can't remember why we were doing the stuff down below but this works.  If we didn't do it, converting
    // from spread to pages would have the safety zone, page selection halo, etc. in the wrong spot
    return this.dim.width;

    // let canvasDiv = this.refs.canvasDiv;
    // // let editorContainer = document.getElementById('editorContainer');
    // // let cwidth = 0;
    // // if(this.props.twoPage) {
    // //     cwidth = (editorContainer.clientWidth-0)/2;
    // // } else {
    // //     cwidth = editorContainer.clientWidth;
    // // }
    // // return cwidth;
    //
    // return canvasDiv.clientWidth;
  };

  isBook() {
    const projectType = this.props.project.get('type');
    return (
      projectType === PicabooConstants.PRODUCT_TYPES.BOOK ||
      projectType === PicabooConstants.PRODUCT_TYPES.YEARBOOK ||
      projectType === PicabooConstants.PRODUCT_TYPES.PERSONALIZED_YEARBOOK
    );
  }

  projectType() {
    return this.props.project.get('type');
  }

  isCard() {
    return this.projectType() === PicabooConstants.PRODUCT_TYPES.CARD;
  }

  isFrame() {
    return this.projectType() === PicabooConstants.PRODUCT_TYPES.FRAME;
  }

  isCanvas() {
    return this.projectType() === PicabooConstants.PRODUCT_TYPES.CANVAS;
  }

  isCoverSection = () => {
    if (this.props.reviewMode && !this.props.reviewSection) {
      return false;
    } else if (!this.props.section) {
      return false;
    }
    const sectionType = this.props.reviewMode
      ? this.props.reviewSection.get('type')
      : this.props.section.get('type');
    return (
      sectionType === PicabooConstants.SECTION_TYPES.TYPE_COVER ||
      sectionType === PicabooConstants.SECTION_TYPES.TYPE_SOFTCOVER
    );
  };

  isHardCover = () => {
    if (this.props.section === undefined) {
      return false;
    }
    const sectionType = this.props.section.get('type');
    return sectionType === PicabooConstants.SECTION_TYPES.TYPE_COVER;
  };

  hasNoSafetyZone = () => {
    const productType = this.props.project.get('type');
    return productType == PicabooConstants.PRODUCT_TYPES.CANVAS;
  };

  toggleWellMenu() {
    this.setState({ showWellMenu: !this.state.showWellMenu });
  }
  showEditPhotoPanel() {
    this.props.dispatch(setSidePanelOpened(true));
    this.props.dispatch(setSelectedTab(TAB_EDIT_PHOTO));
  }

  deletePhoto(e) {
    this.props.dispatch(deleteSelectedPageItems());
    e.preventDefault();
  }

  //noinspection JSMethodCanBeStatic
  render() {
    let currentPage = this.getPage();
    if (!currentPage) {
      return null;
    }

    const displayOverlay = this.props.selection.eyeDropperEnabled ? 'none' : '';

    //get the height of the containing element
    let workspaceContainer = document.getElementById('workspaceContainer');
    let calculatedCanvasHeight = null;
    if (workspaceContainer != undefined) {
      let workspaceHeight = workspaceContainer.offsetHeight;
      calculatedCanvasHeight = workspaceHeight - 90; //50 for the toolbar and 40 for top and bottom margins...
    }

    let showPageSelection =
      !this.props.reviewMode &&
      this.currentPageIsSelected() &&
      (this.props.twoPage || this.isBook());
    let insideCover = null;
    if (
      !this.isCoverSection() &&
      this.isBook() &&
      currentPage.get('pageType') === PicabooConstants.PAGE_TYPES.COVER
    ) {
      insideCover = (
        <InsideCoverOverlay
          productInfo={this.props.productInfo}
          spreadWidth={this.state.pageWidth}
          frontInside={this.props.leftPageNumber <= 1}
          isPUR={isPURBook(this.props.project.get('style'))}
          currentPage={currentPage}
        />
      );
    }

    let noEditOverlay = null;
    if (currentPage.get('belongsTo') != undefined) {
      const belongsTo = currentPage.get('belongsTo').toJS();
      noEditOverlay = (
        <div className={styles.noEdits}>
          <div>{belongsTo.title}</div>
        </div>
      );
    }

    let hideTop = false,
      hideLeft = false,
      hideRight = false,
      hideBottom = false,
      showCenter = false;

    if (this.isBook()) {
      if (
        currentPage.get('ratioBase') === 2 &&
        (!this.props.selectedSize ||
          !this.props.selectedSize.has('showGutterSafety') ||
          this.props.selectedSize.get('showGutterSafety'))
      ) {
        showCenter = true;
      } else if (
        currentPage.get('ratioBase') !== 2 &&
        this.props.selectedSize
      ) {
        if (
          currentPage.get('pageType') !== 'C' &&
          currentPage.has('pageNumber')
        ) {
          if (currentPage.get('pageNumber').get(0) % 2 === 0) {
            hideLeft = true;
          } else {
            hideRight = true;
          }
        }
      }
    }

    let isFrame = this.isFrame();
    let isCanvas = this.isCanvas();

    let theStyle = {
      display: 'none',
      position: 'relative',
      outlineStyle: 'none',
      visibility: 'hidden',
      border: '',
    };

    if (isCanvas) {
      const FRAME_WIDTH_INCHES = 0.385;
      let frameTextureImage = frameTextureImages();

      let size = this.props.productInfo.get('frameSize');
      theStyle.borderWidth = `${calculatedCanvasHeight * (FRAME_WIDTH_INCHES / size.get('height'))}px`;

      let coverColor = this.props.project.get('cover');
      let currentCover = this.props.productInfo
        .get('frameColors')
        .find((c) => c.get('pk') === coverColor);
      theStyle.borderImage = `url("${frameTextureImage[currentCover.get('pk')]}") 140`;
      theStyle.border = '12px solid black';
    }

    if (isFrame) {
      const FRAME_WIDTH_INCHES = 3 / 4;
      let frameTextureImage = frameTextureImages();

      let size = this.props.productInfo.get('frameSize');
      theStyle.borderWidth = `${calculatedCanvasHeight * (FRAME_WIDTH_INCHES / size.get('height'))}px`;

      let coverColor = this.props.project.get('cover');
      let currentCover = this.props.productInfo
        .get('frameColors')
        .find((c) => c.get('pk') === coverColor);
      theStyle.borderImage = `url("${frameTextureImage[currentCover.get('pk')]}") 40`;
      theStyle.border = '2px solid black';
    }

    // forwardRef again here!
    // Dropdown needs access to the DOM of the Menu to measure it
    const CustomMenu = React.forwardRef((props, ref) => {
      props.style.position = 'fixed';
      props.style.left = this.state.wellMenuCoords.x;
      props.style.top = this.state.wellMenuCoords.y;
      props.style.bottom = 'auto';
      props.style.right = 'auto';

      let elem = document.querySelector('#' + props.id);
      if (elem) {
        let menuWidth = elem.getBoundingClientRect().width;
        if (this.state.wellMenuCoords.x + menuWidth > this.props.viewport) {
          props.style.transform = `translate(-${menuWidth}px, 0px)`; // If menu goes off screen translate to opposite side
        }
      }

      return (
        props.show && (
          <div
            ref={ref}
            style={props.style}
            className={props.className}
            aria-labelledby={props.labeledBy}
            id={props.id}
            onMouseLeave={this.toggleWellMenu.bind(this)}
          >
            <ul className="list-unstyled">
              {React.Children.toArray(props.children)}
            </ul>
          </div>
        )
      );
    });

    // The forwardRef is important!!
    // Dropdown needs access to the DOM node in order to position the Menu
    const CustomToggle = React.forwardRef((props, ref) => {
      function toggle(e) {
        e.preventDefault();
        e.stopPropagation();
        e.bubbles = false;
        e.cancelBubble = true;
        props.onClick(e);
      }
      return (
        <a
          href=""
          ref={ref}
          id={props.id}
          onMouseEnter={(e) => {
            toggle(e);
          }}
          onClick={(e) => {
            toggle(e);
          }}
        >
          {props.children}
        </a>
      );
    });

    const currentPageNumber = currentPage.get('pageNumber')
      ? currentPage.get('pageNumber').get(0)
      : 0;

    const pageWidth = this.state.pageWidth;

    const widthBase = Math.max(pageWidth, this.state.containerWidth);

    const leftPageEdge = (widthBase - pageWidth) / 2;

    let isSoftCover =
      this.props.section.get('type') ===
      PicabooConstants.SECTION_TYPES.TYPE_SOFTCOVER;

    let spineWidth = SpineHelper.calculateSpineWidth(
      this.state.pageWidth,
      this.props.project.get('numPages'),
      this.props.project.get('subtype'),
      this.props.project.get('style'),
      this.props.productInfo,
    );

    const leftSpine = (this.state.pageWidth - spineWidth) / 2;

    const spineWidthMax = SpineHelper.calculateMaxSpineWidth(
      this.state.pageWidth,
      this.props.project.get('subtype'),
      this.props.project.get('style'),
      this.props.productInfo,
    );

    const outerAdjustment = this.props.productInfo.get('dustJacket')
      ? 0
      : (spineWidthMax - spineWidth) / 2;

    const horizontalWrap = this.props.productInfo.get('dustJacket')
      ? 0
      : this.props.wrap.get('horizontalWrap') * this.state.pageWidth;

    return (
      <div
        id={'canvasDiv' + this.props.uid}
        tabIndex={this.props.uid}
        onKeyDown={this.handleKeyDown}
        onDragOver={this.handleDragOver}
        style={theStyle}
      >
        {insideCover}
        <div
          id={'canvasDivInner' + this.props.uid}
          style={{
            position: 'relative',
            width: this.dim ? this.dim.widthWithMargin : 0,
            height: this.dim ? this.dim.heightWithMargin : 0,
            paddingLeft: this.dim ? this.dim.marginX : 0,
            marginLeft: this.dim && this.dim.marginX > 0 ? 5 : 0,
            marginRight: this.dim && this.dim.marginX > 0 ? 5 : 0,
            paddingTop: this.dim ? this.dim.marginY : 0,
            boxShadow: isCanvas
              ? '0px 0px 0px 3px #484848'
              : '0px 0px 3px -2px #221F20',
            backgroundColor:
              this.dim && this.dim.marginX > 0 ? '#ffffff' : 'transparent',
          }}
        >
          <canvas id={'c' + this.props.uid} />

          <div
            id="eyeDropperFloater"
            className={styles.eyeDropperContainer}
            style={{ display: displayOverlay, zIndex: 2011 }}
          >
            <div id="eyeDropperIcon" className={styles.eyeDropperIcon}></div>
            <div>
              <div
                className={styles.eyeDropperHexColor}
                id="eyeDropperHexColor"
              ></div>
              <div className={styles.eyeDropperText} id="eyeDropperText"></div>
            </div>
          </div>
          {this.getConvertModal(this.props.t)}
          {currentPage && !this.props.previewMode && !this.props.reviewMode ? (
            this.isCoverSection() ? (
              <WrapArea
                pageWidth={this.state.pageWidth}
                pageHeight={this.state.pageHeight}
                page={this.props.page}
                containerWidth={this.state.containerWidth}
                containerHeight={this.state.containerHeight}
                scale={this.dim ? this.dim.scale : 1}
                reviewMode={
                  this.props.reviewMode && currentPage.get('pageType') !== 'C'
                }
                wrap={this.props.wrap}
                productInfo={this.props.productInfo}
                leftPageEdge={leftPageEdge}
                horizontalWrap={horizontalWrap}
                leftSpine={leftSpine}
                isSoftCover={isSoftCover}
                labelVisible={
                  this.state.showSafety || this.props.settings.get('showSafety')
                }
                t={this.props.t}
              />
            ) : (
              <ZoneOverlay
                pageWidth={this.state.pageWidth}
                pageHeight={this.state.pageHeight}
                containerWidth={this.state.containerWidth}
                containerHeight={this.state.containerHeight}
                cropVisible={
                  (this.state.showSafety ||
                    this.props.settings.get('showSafety')) &&
                  currentPage.get('pageType') !== 'C'
                }
                labelVisible={
                  this.state.showSafety || this.props.settings.get('showSafety')
                }
                warningVisible={this.state.showSafety}
                ratioBase={currentPage.get('ratioBase')}
                hideLeft={hideLeft}
                hideTop={hideTop}
                hideRight={hideRight}
                hideBottom={hideBottom}
                showCenter={showCenter}
                previewMode={
                  (this.props.previewMode || this.props.reviewMode) &&
                  currentPage.get('pageType') !== 'C'
                }
                insideCoverShowing={this.insideCoverShowing()}
                twoPage={this.props.twoPage}
                pageNumber={currentPageNumber}
                isCover={this.isCoverSection()}
                t={this.props.t}
              />
            )
          ) : null}

          <Dropdown show={this.state.showWellMenu}>
            <Dropdown.Menu
              onBlur={this.toggleWellMenu.bind(this)}
              id={'well-menu'}
              as={CustomMenu}
            >
              <Dropdown.Header>
                <div>Options</div>
                <div>
                  <MaterialIcon
                    onClick={this.toggleWellMenu.bind(this)}
                    icon="close"
                    classOverride={styles.close}
                  />
                </div>
              </Dropdown.Header>
              <Dropdown.Item
                onClick={this.showEditPhotoPanel.bind(this)}
                eventKey="1"
              >
                {this.props.t(
                  'EDITORCONTAINER.WORKSPACECONTAINER.CANVASCONTAINER.Edit Photo',
                )}
              </Dropdown.Item>
              <Dropdown.Item onClick={this.deletePhoto.bind(this)} eventKey="2">
                {this.props.t(
                  'EDITORCONTAINER.WORKSPACECONTAINER.CANVASCONTAINER.Delete Photo',
                )}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <SelectionOverlay
            pageWidth={this.state.pageWidth}
            pageHeight={this.state.pageHeight}
            containerWidth={this.state.containerWidth}
            containerHeight={this.state.containerHeight}
            marginLeft={this.dim ? this.dim.marginX : 0}
            marginTop={this.dim ? this.dim.marginY : 0}
            visible={showPageSelection}
            outerAdjustment={outerAdjustment}
            isCover={this.isCoverSection()}
          />

          {!this.props.previewMode && !this.props.reviewMode && (
            <SnapOverlay
              containerWidth={this.state.containerWidth}
              containerHeight={this.state.containerHeight}
              pageWidth={this.state.pageWidth}
              pageHeight={this.state.pageHeight}
              drawHorizontal={this.state.showHorizontalSnap}
              drawVertical={this.state.showVerticalSnap}
              xPoint={this.state.snapX}
              yPoint={this.state.snapY}
              offSetX={this.state.snapOffSetX}
              offSetY={this.state.snapOffSetY}
            />
          )}
        </div>
        {noEditOverlay}
      </div>
    );
  }

  getConvertModal = (t) => {
    if (this.state.showConvertDialog)
      return (
        <PicabooModal show={this.state.showConvertDialog}>
          <Modal.Header>
            {t(
              'EDITORCONTAINER.WORKSPACECONTAINER.CANVASCONTAINER.convertToSpreadModal.convertToSpread',
            )}
            ?
          </Modal.Header>
          <Modal.Body>
            <div>
              {t(
                'EDITORCONTAINER.WORKSPACECONTAINER.CANVASCONTAINER.convertToSpreadModal.body',
              )}
            </div>
            <br />
            <div>
              {t(
                'EDITORCONTAINER.WORKSPACECONTAINER.CANVASCONTAINER.convertToSpreadModal.warning',
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              onClick={this.closeConvertDialog.bind(this)}
              variant={'link'}
            >
              {t(
                'EDITORCONTAINER.WORKSPACECONTAINER.CANVASCONTAINER.convertToSpreadModal.cancel',
              )}
            </Button>
            <Button onClick={this.convertToSpread.bind(this)} variant="primary">
              {t(
                'EDITORCONTAINER.WORKSPACECONTAINER.CANVASCONTAINER.convertToSpreadModal.convertToSpread',
              )}
            </Button>
          </Modal.Footer>
        </PicabooModal>
      );

    return null;
  };

  closeConvertDialog = () => {
    this.updatePageItems(this.state.convertDialogItems, true, false);
    this.setState({
      showConvertDialog: false,
      convertDialogItems: null,
      convertDialogUpdatedItems: null,
    });
    logEvent('Page', 'Convert to spread for 2-page element modal', 'Cancel', 0);
  };

  convertToSpread = () => {
    this.setState({
      showConvertDialog: false,
      convertDialogItems: null,
      convertDialogUpdatedItems: null,
    });
    this.props.dispatch(convertSpreadPages());
    logEvent(
      'Page',
      'Convert to spread for 2-page element modal',
      'Convert',
      0,
    );
  };

  redrawCanvas = () => {
    if (this.props.wrap.get('loaded')) {
      this.canvas.clear();
      this.renderCanvas = -1;
      this.setPageRenderer(this.props);
    }
  };

  makeNonInteractive() {
    if (this.renderer) {
      this.renderer.updateInteractive(false);
    }
  }

  makeInteractive() {
    if (this.renderer) {
      this.renderer.updateInteractive(true);
    }
  }

  canvasSizeToPageItemSize(canvasSize) {
    return canvasSize / this.dim.ratioWidth;
  }

  pageItemUpdated = (item, undoable = true) => {
    if (!this.props.editTextModalOpen) {
      if (this.props.pageItems.has(item.pageItemVO.get('id'))) {
        this.updatePageItems(item, undoable);
      }
    }

    this.addUpdateSnapPoints(item.pageItemVO.get('id'), item);
  };

  updatePageItem = (canvasPageItem) => {
    const pageVO = this.getPage();
    if (canvasPageItem === null || pageVO === null) return;
    let pageItemVO = canvasPageItem.pageItemVO;
    if (!pageItemVO) return;
    let itemType = pageItemVO.get('itemType');

    try {
      let dim = this.calcPageDimensions();

      let adjustedDimensions = GraphicItemUtils.adjustImageToWell(
        pageItemVO,
        dim,
        canvasPageItem,
      );

      const adjustedVO = pageItemVO.withMutations((list) => {
        let left = canvasPageItem.left;
        let top = canvasPageItem.top;
        let rotation = canvasPageItem.angle;
        let width = this.canvasSizeToPageItemSize(canvasPageItem.width);
        let height = this.canvasSizeToPageItemSize(canvasPageItem.height);

        // let leftDiff = 0;
        // let topDiff = 0;

        //Multi-selection groups set items to use coordinates that originate from the center of the group
        if (canvasPageItem.group) {
          left =
            canvasPageItem.group.left +
            canvasPageItem.group.width / 2 +
            canvasPageItem.left;
          top =
            canvasPageItem.group.top +
            canvasPageItem.group.height / 2 +
            canvasPageItem.top;

          //Since this is part of a group selection we know the dimensions/rotation have not changed
          rotation = pageItemVO.get('rotation') || 0;

          if (
            pageItemVO.get('itemType') !== PicabooConstants.ITEM_TYPES.RICH_TEXT
          ) {
            width = pageItemVO.get('width');
            height = pageItemVO.get('height');
          }

          // const rad = canvasPageItem.angle * (Math.PI / 180);
          //
          // // hyp is distance from center to top left corner... a^2 + b^2 = c^2... c = sqrt( a^2 + b^2)
          // // a = width/2, b = height/2
          // // hyp = c = sqrt( (width/2)^2 + (height/2)^2 )
          // // or we calc the hyp for the whole thing and 1/2 it...
          // // hyp = sqrt( width^2 + height^2 ) / 2
          // const hyp = Math.sqrt(Math.pow(canvasPageItem.width,2) + Math.pow(canvasPageItem.height, 2))/2;
          //
          // // get the angle from the top left to center of the unroated item
          // const rad2 = Math.atan2(canvasPageItem.height, canvasPageItem.width);
          //
          // // add both angles to get the final angle
          // const fullRad = rad + rad2;
          //
          // // now get stuff based off full rad
          // leftDiff = hyp * Math.cos(fullRad);
          // topDiff = hyp * Math.sin(fullRad);
          //
          // left = left - leftDiff;
          // top = top - topDiff;
        } else if (canvasPageItem.originX == 'center') {
          let translatedPoint = canvasPageItem.getPointByOrigin('left', 'top');
          left = translatedPoint.x;
          top = translatedPoint.y;
        } else if (itemType == 'line') {
          //When we draw a line, we subtract the strokeWidth from the width, so now we have to add it back in.

          let strokeWidth =
            (list.get('thickness') * this.dim.scale) / this.dim.ratioBase;
          let totalWidth =
            canvasPageItem.width * canvasPageItem.scaleX +
            strokeWidth * canvasPageItem.scaleX;
          width = this.canvasSizeToPageItemSize(totalWidth);
        }

        if (
          pageItemVO.get('itemType') === PicabooConstants.ITEM_TYPES.RICH_TEXT
        ) {
          width *= dim.scale;
          height *= dim.scale;
        }

        list
          .set('positionX', this.canvasSizeToPageItemSize(left))
          .set('positionY', this.canvasSizeToPageItemSize(top))
          .set('width', width)
          .set('height', height)
          .set('rotation', rotation)
          .set('image_width', adjustedDimensions.width / dim.ratioWidth)
          .set('image_height', adjustedDimensions.height / dim.ratioWidth)
          .set('image_positionX', adjustedDimensions.x / dim.ratioWidth)
          .set('image_positionY', adjustedDimensions.y / dim.ratioWidth);

        if (
          pageItemVO.get('itemType') === PicabooConstants.ITEM_TYPES.RICH_TEXT
        ) {
          const captionText =
            canvasPageItem.text === PicabooConstants.DEFAULT_RT_CAPTION
              ? ''
              : canvasPageItem.text;
          list.set('text', captionText);
          if (canvasPageItem.ignoredWords) {
            list.set('ignoredWords', canvasPageItem.ignoredWords);
          }

          const adjustedStyles = FabricUtils.adjustFontSizes(
            canvasPageItem.styles,
            false,
            this.props.productInfo,
          );

          list.set('styles', fromJS(adjustedStyles));
          list.set('textAlign', canvasPageItem.textAlign);
          list.set('showBackground', canvasPageItem.backgroundColor != null);
          list.set('backgroundColor', canvasPageItem.backgroundColor);
        }
      });

      if (!canvasPageItem.group) {
        if (itemType == PicabooConstants.ITEM_TYPES.CAPTION) {
          //Only need to redraw if item has been resized
          if (
            pageItemVO.get('width').toFixed(5) !=
              adjustedVO.get('width').toFixed(5) ||
            pageItemVO.get('height').toFixed(5) !=
              adjustedVO.get('height').toFixed(5)
          ) {
            this.renderer.redrawPageItem(adjustedVO);
          }
        }
      }

      canvasPageItem.pageItemVO = adjustedVO;
      return adjustedVO;
    } catch (error) {
      console.error('Error in updatePageItem', error);
      return pageItemVO;
    }
  };

  updatePageItems = (
    selectedItems,
    undoable = true,
    showConvertDialogIfRequired = true,
  ) => {
    // get an array of all the selected canvas items themselves.. not the page items as in
    // this.getSelectedItems().. this is very similar though
    let items = [];

    if (this.getSelectedItems().length > 1) {
      for (let obj of selectedItems.getObjects()) {
        items.push(obj);
      }
    } else {
      // shape or caption
      // this.resetItemOrigin(selectedItems);
      items.push(selectedItems);
    }

    let updatedItems = items.map(this.updatePageItem);

    let updateCanvas = false;

    this.setState({
      showConvertSpreadDialog: false,
    });

    let bothPagesSameSection = true;
    this.props.pages.forEach((p) => {
      if (p && p.get('belongsTo') !== undefined) {
        bothPagesSameSection = false;
      }
    });

    let invalidPages = this.props.pages.some(
      (page) => page.get('pageType') != PicabooConstants.PAGE_TYPES.NORMAL,
    );

    updatedItems = updatedItems.map(
      function (item) {
        if (
          !invalidPages &&
          bothPagesSameSection &&
          showConvertDialogIfRequired &&
          !this.insideCoverShowing() &&
          !this.state.showConvertSpreadDialog &&
          this.props.isBook &&
          this.props.twoPage &&
          item.get('itemType') !== PicabooConstants.ITEM_TYPES.GRAPHICITEM
        ) {
          if (this.props.uid === '1') {
            // right page
            if (item.get('positionX') < -item.get('width') / 5) {
              this.setState({
                showConvertSpreadDialog: true,
              });
            }
          } else {
            // left page
            if (
              item.get('positionX') + item.get('width') >
              1 + item.get('width') / 5
            ) {
              this.setState({
                showConvertSpreadDialog: true,
              });
            }
          }
        }

        if (
          !this.state.showConvertSpreadDialog &&
          GraphicItemUtils.isOffPage(
            item,
            this.getPage().get('ratio'),
            this.getPage().get('ratioBase'),
          )
        ) {
          item = item.withMutations((list) => {
            list.set('positionX', 0.05).set('positionY', 0.05);
          });
          updateCanvas = true;
        }
        return item;
      }.bind(this),
    );

    if (this.state.showConvertSpreadDialog) {
      this.props.dispatch(updatePageItems(updatedItems));

      this.setState({
        showConvertDialog: true,
        convertDialogItems: selectedItems,
        convertDialogUpdatedItems: updatedItems,
      });
    } else {
      // this.canvas.deactivateAll();
      this.props.dispatch(
        undoable
          ? updatePageItems(updatedItems)
          : updatePageItemsNoUndo(updatedItems),
      );

      if (updateCanvas) {
        this.resizeTimeout = setTimeout(
          function () {
            this.redrawCanvas();
          }.bind(this),
          50,
        );
      }
    }
  };

  handleUpdateWarnings = (warnings) => {
    try {
      let { dispatch } = this.props;
      dispatch(updateWarnings(warnings));
    } catch (error) {
      console.error('Selection error', error);
    }
  };

  insideCoverShowing() {
    let isInside = false;
    this.props.pages.some((page) => {
      isInside =
        isInside || page.get('pageType') == PicabooConstants.PAGE_TYPES.COVER;
    });
    return isInside;
  }

  selectionChanged = (options) => {
    this.canvas.preserveObjectStacking = true;
    let selected = options.selected[0];
    try {
      let { dispatch, pageItems } = this.props;
      if (selected) {
        let selectedItems = this.getSelectedItems();
        // BWO-135 Prevent group selection by discarding selected objects on group selection
        if (this.canvas.getActiveObjects().length > 1) {
          this.canvas.discardActiveObject();
          return;
        }
        if (selectedItems.length > 1) {
          //This will be handled in 'selectionUpdated' handler
          this.dispatchSelection(selectedItems);

          selected.transparentCorners = true;
          selected.hasRotationPoint = false;
          selected.hasControls = false;
          selected.borderColor = '#595959';

          this.checkSafetyZone(selected);
        } else {
          this.dispatchSelection(selectedItems);
          switch (pageItems.get(selectedItems[0]).get('itemType')) {
            case PicabooConstants.ITEM_TYPES.RICH_TEXT:
              selected.lockScalingY = true;
              selected.setControlsVisibility({
                bl: false,
                br: false,
                mb: false,
                ml: true,
                mr: true,
                mt: false,
                tl: false,
                tr: false,
                mtr: true,
              });

              if (isMobile()) {
                selected.editable = false;
              }
              dispatch(setSelectedTab(TAB_EDIT_RICH_TEXT));

              break;
            default:
              if (isMobile()) {
                dispatch(setSidePanelOpened(false));
              }
              break;
          }
        }
      } else {
        this.dispatchSelection([]);
      }
      this.checkSafetyZone(selected);
    } catch (error) {
      console.error('Selection error', error);
    }
  };

  dispatchSelection = (pageItems) => {
    let { dispatch } = this.props;
    if (!this.currentPageIsSelected()) {
      dispatch(selectPages([this.getPage().get('id')]));
    }

    if (isMobile()) {
      if (
        this.props.project.get('type') == PicabooConstants.PRODUCT_TYPES.PRINTS
      ) {
        dispatch(setSidePanelOpened(true));
      } else {
        if (pageItems && pageItems.length === 1) {
          setTimeout(() => {
            const pageItem = this.props.pageItems.get(pageItems[0]);
            if (
              pageItem &&
              pageItem.get('itemType') ===
                PicabooConstants.ITEM_TYPES.GRAPHICITEM &&
              !pageItem.get('imageID')
            ) {
              if (!this.props.showImageManagement) {
                dispatch(toggleImageManagement(true));
              }
            } else {
              // dispatch(setSidePanelOpened(true));
            }
          }, 300);
        }
      }
    }

    dispatch(selectPageItems(pageItems));
  };

  currentPageIsSelected = () => {
    try {
      return (
        this.props.selectedPages.find(
          (a) => a.get('id') == this.getPage().get('id'),
        ) !== undefined
      );
    } catch (error) {
      return false;
    }
  };

  getBackgroundGraphicItemId() {
    let page = this.getPage();
    if (page) {
      return (
        page.getIn(['background', 'graphicItem']) ||
        page.get('background').graphicItem
      );
    }

    return null;
  }

  selectBackgroundImage() {
    let selected = false;
    const graphicItem = this.getBackgroundGraphicItemId();
    if (
      graphicItem &&
      (this.getPage().get('canSetBackground') ||
        this.props.project.get('type') == PicabooConstants.PRODUCT_TYPES.PRINTS)
    ) {
      this.dispatchSelection([graphicItem]);
      selected = true;
    } else {
      this.dispatchSelection([]);
    }

    return selected;
  }

  objectAdded = (options) => {
    if (options.target) {
      this.addUpdateSnapPoints(
        options.target.pageItemVO.get('id'),
        options.target,
      );
    }
  };

  objectRemoved = (options) => {
    if (options.target) {
      this.removeSnapPoints(options.target.pageItemVO.get('id'));
    }
  };

  objectModified = (options) => {
    //if the object has a pageItemVO, it's a parent ad, otherwise it's a group...
    if (options.target._objects && !options.target.pageItemVO) {
      //This is a multi-select
      for (let item of options.target._objects) {
        if (
          item.pageItemVO &&
          this.props.pageItems &&
          !this.props.pageItems.has(item.pageItemVO.get('id'))
        ) {
          return;
        }
      }

      this.targetModified(options.target);
    } else if (
      options.target &&
      this.props.pageItems.has(options.target.pageItemVO.get('id'))
    ) {
      this.targetModified(options.target);
    }
  };

  targetModified = (target) => {
    let pi = target?.pageItemVO;
    this.checkSafetyZone(target, true);
    if (pi) {
      this.addUpdateSnapPoints(target.pageItemVO.get('id'), target);
    }

    // Save coords so we can use the original values later (well... _originalTop and _originalLeft)
    target.saveCoords && target.saveCoords();

    //Remove temp cached object if needed
    // if ( options && options.target && options.target.pageItemVO && options.target.pageItemVO.get('imageID')) {
    //     GraphicItemUtils.imageCache[options.target.pageItemVO.get('imageID') + 'R'] = null;
    // }

    if (this.activeResize) {
      this.activeResize = false;

      const resizingFromCorner = this.currentCorner.indexOf('m') == -1;

      this.resizeGraphicItem(target, resizingFromCorner);
      let isLowRes = this.renderer._checkLowRes(
        pi,
        this.isLowRes,
        this.props.project.get('warnings'),
      );
      target.pageItemVO = target.pageItemVO.set('isLowRes', isLowRes);
    }
    this.updatePageItems(target);
    this.activeResize = false;
    this.currentCorner = undefined;
    this.snappingX = false;
    this.snappingY = false;
    this.snapped = false;
  };

  objectScaling = (options, v1, v2, v3) => {
    //The 'target' here can be one of 3 things:
    //1. Some kind of shape object
    //2. A Group object that represents a graphic item
    //3. A caption (image) or richtext (textbox) object

    //process the snap before applying the resize.
    const { target, e } = options;

    const corner =
      target._findTargetCorner &&
      target._findTargetCorner({ x: e.offsetX, y: e.offsetY });

    if (corner) {
      this.currentCorner = corner;
    } else if (options.transform && options.transform.corner) {
      this.currentCorner = options.transform.corner;
    }

    if (!this.currentCorner) {
      console.warn('No current corner');
      return;
    }

    const resizingFromCorner =
      this.currentCorner && this.currentCorner.indexOf('m') == -1;

    this.processSnap(target, e, true);

    //  only resize once while in the snap zone
    let canResize = true;
    if (this.snappingX || this.snappingY) {
      if (this.snapped) {
        canResize = false;
      } else {
        this.snapped = true;
      }
    }

    if (canResize) {
      if (target.get('type') === 'group') {
        if (target.pageItemVO) {
          // graphic item
          this.activeResize = true;
          this.resizeGraphicItem(target, resizingFromCorner);
        }
      } else {
        // shape/caption/richtext
        this.resizeScaledObject(target, resizingFromCorner);
      }
    }

    this.checkSafetyZone(target);
  };

  objectMoving = (options) => {
    if (
      options?.target?.pageItemVO?.get('itemType') ==
      PicabooConstants.ITEM_TYPES.GRAPHICITEM
    ) {
      this.restrictObjectBoundary(options.target); // Restrict object boundary only for Graphic Item
    }
    this.processSnap(options.target, false);
    this.checkSafetyZone(options.target);
  };

  objectRotating = (options) => {
    this.sendCoordinates(options.target);
  };

  // Function to calculate vertical difference between two corners
  calculateVerticalDifference(corner1, corner2) {
    return Math.abs(corner1.y - corner2.y);
  }

  // Function to calculate horizontal difference between two corners
  calculateHorizontalDifference(corner1, corner2) {
    return Math.abs(corner1.x - corner2.x);
  }

  // Restrict object movement within canvas boundary
  restrictObjectBoundary(target) {
    target.setCoords();
    let canvasWidth = target.canvas.width;
    let canvasHeight = target.canvas.height;
    let angle = target.angle;

    let boundingBox = target.getBoundingRect(); // Returns true bounding top left after rotation

    const { tl, tr, bl, br } = target.oCoords; // Coords of rotated object where top left references the same corner which is top left at 0 degree
    let minLeft = 0;
    let minTop = 0;
    this.setState({
      showConvertSpreadDialog: false,
    });
    if (angle >= 0 && angle < 90) {
      // 0° <= angle < 90°: Top-left corner is top-left
      // Fix the top-left corner within canvas boundaries
      // Adjust top boundary
      if (boundingBox.top < minTop) {
        target.top = minTop;
      }
      // Adjust left boundary
      if (boundingBox.left < minLeft) {
        target.left = minLeft + this.calculateHorizontalDifference(tl, bl);
        if (this.props.uid == '1' && !this.insideCoverShowing()) {
          this.setState({
            showConvertSpreadDialog: true,
          });
        }
      }
      // Adjust bottom boundary
      if (boundingBox.top + boundingBox.height > target.canvas.height) {
        target.top = target.canvas.height - boundingBox.height;
      }
      // Adjust right boundary
      if (boundingBox.left + boundingBox.width > target.canvas.width) {
        target.left =
          target.canvas.width - this.calculateHorizontalDifference(tl, tr);
        if (this.props.uid == '0' && !this.insideCoverShowing()) {
          this.setState({
            showConvertSpreadDialog: true,
          });
        }
      }
    }

    if (angle >= 90 && angle < 180) {
      // 90° <= angle < 180°: Top-left corner is now the top-right corner of the rotated object

      const yDifference = this.calculateVerticalDifference(tl, bl);
      // Fix the top left (which is the top right of the rotated object) within canvas boundaries
      // Adjust top boundary
      if (boundingBox.top < minTop) {
        target.top = minTop + yDifference;
      }

      // Adjust left boundary
      if (boundingBox.left < minLeft) {
        target.left = boundingBox.width;
        if (this.props.uid == '1' && !this.insideCoverShowing()) {
          this.setState({
            showConvertSpreadDialog: true,
          });
        }
      }

      // Adjust bottom boundary
      if (boundingBox.top + boundingBox.height > target.canvas.height) {
        target.top = target.canvas.height - boundingBox.height + yDifference;
      }

      // Adjust right boundary
      if (boundingBox.left + boundingBox.width > target.canvas.width) {
        target.left = target.canvas.width;
        if (this.props.uid == '0' && !this.insideCoverShowing()) {
          this.setState({
            showConvertSpreadDialog: true,
          });
        }
      }
    }

    if (angle >= 180 && angle < 270) {
      // 180° <= angle < 270°: Top-left corner is now the bottom right corner of the rotated object
      // Fix the top left (which is the bottom right of the rotated object) within canvas boundaries
      // Adjust top boundary
      if (boundingBox.top < minTop) {
        target.top = boundingBox.height;
      }
      // Adjust left boundary
      if (boundingBox.left < minLeft) {
        target.left = this.calculateHorizontalDifference(tl, tr);
        if (this.props.uid == '1') {
          this.setState({
            showConvertSpreadDialog: true,
          });
        }
      }
      // Adjust bottom boundary
      if (boundingBox.top + boundingBox.height > canvasHeight) {
        target.top = canvasHeight;
      }
      // Adjust right boundary
      if (boundingBox.left + boundingBox.width > canvasWidth) {
        target.left = canvasWidth - this.calculateHorizontalDifference(tl, bl);
        if (this.props.uid == '0') {
          this.setState({
            showConvertSpreadDialog: true,
          });
        }
      }
    }

    if (angle >= 270 && angle < 360) {
      // 270° <= angle < 360°: Top-left corner is now the bottom left corner of the rotated object
      // Fix the top left (which is the bottom left of the rotated object) within canvas boundaries
      // Adjust top boundary
      if (boundingBox.top < minTop) {
        target.top = this.calculateVerticalDifference(tl, tr);
      }
      // Adjust left boundary
      if (boundingBox.left < minLeft) {
        target.left = minLeft;
        if (this.props.uid == '1') {
          this.setState({
            showConvertSpreadDialog: true,
          });
        }
      }
      // Adjust bottom boundary
      if (boundingBox.top + boundingBox.height > canvasHeight) {
        target.top = canvasHeight - this.calculateVerticalDifference(tl, bl);
      }
      // Adjust right boundary
      if (boundingBox.left + boundingBox.width > canvasWidth) {
        target.left = canvasWidth - boundingBox.width;
        if (this.props.uid == '0') {
          this.setState({
            showConvertSpreadDialog: true,
          });
        }
      }
    }
  }

  processSnap(target, e, modifySize) {
    if (Math.abs(e.movementY) > 1 || Math.abs(e.movementX) > 1) {
      // don't snap if Carlos is moving the mouse too fast
      return;
    }
    let xLoc = null;
    let yLoc = null;
    let pageItemID = target.pageItemVO && target.pageItemVO.get('id');

    this.setState({ showHorizontalSnap: false });
    this.setState({ showVerticalSnap: false });

    if (
      !this.props.settings.get('snapToMargins') &&
      !this.props.settings.get('snapToGrid') &&
      !this.props.settings.get('snapToObjects') &&
      !this.props.settings.get('snapToCenter') &&
      !this.props.settings.get('snapToColumns') &&
      !this.props.settings.get('snapToGuides')
    ) {
      this.sendCoordinates(target);
      return;
    }

    //bring in the snap points from the grids and guides...
    let consolidatedXLocations = this.snapPoints.xLocations.slice();
    let consolidatedYLocations = this.snapPoints.yLocations.slice();
    const propSnaps = this.props.snap.toJS();
    consolidatedYLocations = consolidatedYLocations
      .slice()
      .concat(propSnaps.ySnapPoints);

    if (this.props.isBook && this.props.twoPage) {
      if (this.props.uid == '1') {
        let rightFiltered = propSnaps.xSnapPoints.filter((point) => {
          return point.loc > this.dim.width;
        });
        rightFiltered.forEach((item) => {
          item.loc -= this.dim.width;
        });
        consolidatedXLocations = consolidatedXLocations
          .slice()
          .concat(rightFiltered);
      } else {
        let leftFiltered = propSnaps.xSnapPoints.filter((point) => {
          return point.loc < this.dim.width;
        });
        consolidatedXLocations = consolidatedXLocations
          .slice()
          .concat(leftFiltered);
      }
    } else {
      consolidatedXLocations = consolidatedXLocations
        .slice()
        .concat(propSnaps.xSnapPoints);
    }

    let effectiveSnapXlocations = [];
    let effectiveSnapYlocations = [];
    let effectiveCenterSnapXlocations = [];
    let effectiveCenterSnapYlocations = [];
    if (this.props.settings.get('snapToCenter')) {
      effectiveCenterSnapXlocations = consolidatedXLocations.filter((loc) => {
        return (
          loc.pageItemID === 'centerX' ||
          loc.pageItemID === 'coverLeft' ||
          loc.pageItemID === 'coverRight'
        );
      });
      effectiveSnapXlocations = effectiveCenterSnapXlocations;
      effectiveCenterSnapYlocations = consolidatedYLocations.filter((loc) => {
        return loc.pageItemID === 'centerY';
      });
      effectiveSnapYlocations = effectiveCenterSnapYlocations;
    }
    if (this.props.settings.get('snapToObjects')) {
      let regex = RegExp(
        '(\\{){0,1}[0-9a-fA-F]{8}\\-?[0-9a-fA-F]{4}\\-?[0-9a-fA-F]{4}\\-?[0-9a-fA-F]{4}\\-?[0-9a-fA-F]{12}(\\}){0,1}',
      );
      effectiveSnapXlocations = effectiveSnapXlocations.concat(
        consolidatedXLocations.filter((loc) => {
          return regex.test(loc.pageItemID);
        }),
      );
      effectiveSnapYlocations = effectiveSnapYlocations.concat(
        consolidatedYLocations.filter((loc) => {
          return regex.test(loc.pageItemID);
        }),
      );
    }
    //pick up the rest of the locations. these only exist if they're enabled, so grab them all..
    effectiveSnapXlocations = effectiveSnapXlocations.concat(
      consolidatedXLocations.filter((loc) => {
        return (
          loc.pageItemID &&
          (loc.pageItemID.startsWith('horizontalGrid') ||
            loc.pageItemID.startsWith('margin') ||
            loc.pageItemID.startsWith('column') ||
            loc.pageItemID.startsWith('guide') ||
            loc.pageItemID.startsWith('page'))
        );
      }),
    );
    effectiveSnapYlocations = effectiveSnapYlocations.concat(
      consolidatedYLocations.filter((loc) => {
        return (
          loc.pageItemID &&
          (loc.pageItemID.startsWith('verticalGrid') ||
            loc.pageItemID.startsWith('margin') ||
            loc.pageItemID.startsWith('column') ||
            loc.pageItemID.startsWith('guide') ||
            loc.pageItemID.startsWith('page'))
        );
      }),
    );

    let borderBuffer = 0;
    if (target != undefined) {
      if (target.get('type') === 'group') {
        // getBoundingRect seems to only apply to the last object in a group.  for GraphicItems, this will exclude the border
        if (target.pageItemVO) {
          borderBuffer = this.getActualBorderFromItem(target.pageItemVO);
        }
      }
    }

    target.setCoords();
    const boundRect = target.getBoundingRect();
    const targetRect = {
      x: boundRect.left + target.padding - borderBuffer,
      y: boundRect.top + target.padding - borderBuffer,
      width: boundRect.width - target.padding * 2 + borderBuffer * 2,
      height: boundRect.height - target.padding * 2 + borderBuffer * 2,
      rotation: target.angle,
    };

    if (modifySize) {
      const widthChanged = target.scaleX !== 1;
      const heightChanged = target.scaleY !== 1;

      let oldLockValue = target.lockUniScaling;

      const corner = this.currentCorner; //target._findTargetCorner && target._findTargetCorner({x: e.offsetX, y:e.offsetY});
      // try to figure it out based on original stuff... not preferable but a good fallback
      // we get slight diff when resizing but rounding to 5 decimal points is good. Floating point error
      const xChanged =
        Math.round(target.left * 10000) -
          Math.round(target._originalLeft * 10000) !=
        0;
      const yChanged =
        Math.round(target.top * 10000) -
          Math.round(target._originalTop * 10000) !=
        0;

      let handle = {};
      if (corner != false) {
        if (corner.indexOf('t') !== -1) handle.N = true;
        if (corner.indexOf('b') !== -1) handle.S = true;
        if (corner.indexOf('l') !== -1) handle.W = true;
        if (corner.indexOf('r') !== -1) handle.E = true;
      }

      if (
        (handle.N && handle.E) ||
        (handle.N && handle.W) ||
        (handle.S && handle.E) ||
        (handle.S && handle.W)
      ) {
        target.set({ lockUniScaling: true });
      }

      if ((handle.W || handle.E) && !this.snappingY) {
        if (target.lockUniScaling) {
          if (handle.W) {
            xLoc = this.snapForVerticalLine(
              effectiveSnapXlocations,
              target,
              [targetRect.x],
              handle,
              borderBuffer,
            );
          }
          if (handle.E) {
            xLoc = this.snapForVerticalLine(
              effectiveSnapXlocations,
              target,
              [targetRect.x + targetRect.width],
              handle,
              borderBuffer,
            );
          }
        } else {
          if (targetRect.rotation === 0) {
            xLoc = this.snapForAdjustedWidth(
              effectiveSnapXlocations,
              target,
              targetRect,
              handle,
              borderBuffer,
            );
          }
        }
      }

      if ((handle.N || handle.S) && xLoc == null && !this.snappingX) {
        if (target.lockUniScaling) {
          if (handle.N) {
            yLoc = this.snapForHorizontalLine(
              effectiveSnapYlocations,
              target,
              [targetRect.y],
              handle,
              borderBuffer,
            );
          }
          if (handle.S) {
            yLoc = this.snapForHorizontalLine(
              effectiveSnapYlocations,
              target,
              [targetRect.y + targetRect.height],
              handle,
              borderBuffer,
            );
          }
        } else {
          if (targetRect.rotation === 0) {
            yLoc = this.snapForAdjustedHeight(
              effectiveSnapYlocations,
              target,
              targetRect,
              handle,
              borderBuffer,
            );
          }
        }
      }

      target.set({ lockUniScaling: false });
      target.setCoords();
      target.set({ lockUniScaling: oldLockValue });
    } else {
      const xPoints = [
        targetRect.x,
        targetRect.x + targetRect.width,
        targetRect.x + targetRect.width / 2,
      ];
      xLoc = this.snapForMove(effectiveSnapXlocations, target, xPoints, 'left');
      if (xLoc === null) {
        xLoc = this.snapForMove(
          effectiveCenterSnapXlocations,
          target,
          [targetRect.x + targetRect.width / 2],
          'left',
        );
      }

      const yPoints = [targetRect.y, targetRect.y + targetRect.height];
      yLoc = this.snapForMove(effectiveSnapYlocations, target, yPoints, 'top');
      if (yLoc === undefined) {
        yLoc = this.snapForMove(
          effectiveCenterSnapYlocations,
          target,
          [targetRect.y + targetRect.height / 2],
          'top',
        );
      }
    }

    if (xLoc != null) {
      this.drawVerticalSnapLine(xLoc, this.dim.marginX);
    }

    if (yLoc != null) {
      let offset = 0;
      this.drawHorizontalSnapLine(yLoc, offset);
    }
    this.sendCoordinates(target);
  }

  sendCoordinates(target) {
    const coordinates = {
      x: target.left / this.dim.ratioWidth,
      y: target.top / this.dim.ratioWidth,
      width: target.width / this.dim.ratioWidth,
      height: target.height / this.dim.ratioWidth,
      rotation: target.angle,
    };

    CoordinateHelper.instance.setCoordinates(coordinates);
    // this.props.dispatch(setCoordinates(coordinates))
  }

  // basically old method for snapping while moving
  snapForMove(locations, target, targetLocations, propertyToSnap) {
    if (this.getSelectedItems().length > 1) {
      return;
    }

    let adjusted = false;
    let snappedLoc = undefined;

    for (let i = 0; i < targetLocations.length; ++i) {
      const targetLocation = targetLocations[i];

      for (let j = 0; j < locations.length; ++j) {
        const location = locations[j];
        if (
          target.pageItemVO &&
          target.pageItemVO.get('id') != location.pageItemID
        ) {
          if (!adjusted) {
            const snapToNumber = location.loc;

            const diff = targetLocation - snapToNumber;

            if (Math.abs(diff) < location.tolerance) {
              // THIS HAS TO BE HERE. THIS IS FOR MOVE SNAPPING. PLEASE DON'T DELETE
              let newProps = {};
              newProps[propertyToSnap] = target.get(propertyToSnap) - diff;
              target.set(newProps);
              snappedLoc = location.loc;
              adjusted = true;
              break;
            }
          }
        }
      }
      if (adjusted) break;
    }
    return snappedLoc;
  }

  snapForVerticalLine(
    locations,
    target,
    targetLocations,
    handle,
    borderBuffer = 0,
  ) {
    let adjusted = false;
    let snappedLoc = undefined;
    let inSnapRange = false;
    let isTextbox = target.get('type') === 'textbox'; //rich text does not maintain a scale of 1

    for (let i = 0; i < targetLocations.length; ++i) {
      const targetLocation = targetLocations[i];

      for (let j = 0; j < locations.length; ++j) {
        const location = locations[j];
        if (target.pageItemVO.get('id') == location.pageItemID) continue;

        if (!adjusted) {
          const snapToNumber = location.loc;
          const diff = targetLocation - snapToNumber;

          if (Math.abs(diff) < location.tolerance) {
            target.setCoords();

            inSnapRange = true;
            let newProps = {};
            let diffRatio = (target.width + diff) / target.width;
            let xFactor = -1;

            //  delta = diff * scaleX
            // ratio = scaleX / (scaleY + delta)

            if (handle.E) {
              if (isTextbox) {
                newProps['width'] = target.width + diff / target.scaleX;
                newProps['width'] =
                  (snapToNumber - target.left) / target.scaleX;
              } else {
                xFactor = 1;
                if (!this.snapped) {
                  diffRatio = target.width / (target.width + diff);
                  newProps['scaleX'] = target.scaleX * diffRatio;
                } else {
                  newProps['scaleX'] = 1;
                }
              }
            }

            if (handle.S) {
              if (!this.snapped) {
                newProps['scaleY'] = target.scaleY * diffRatio;
              } else {
                newProps['scaleY'] = 1;
              }
            }

            if (handle.W) {
              xFactor = -1;
              const twistedLeftDiff =
                target.left - target.getBoundingRect(true, true).left;
              let twistedAdjustment = 0;
              if (twistedLeftDiff > 1) {
                twistedAdjustment = twistedLeftDiff;
              }
              newProps['left'] =
                snapToNumber + borderBuffer + twistedAdjustment;
              if (isTextbox) {
                newProps['width'] =
                  target.width +
                  (target.left - newProps['left']) / target.scaleX;
              } else {
                if (!this.snappingX) {
                  newProps['scaleX'] = target.scaleX + diff / target.width;
                } else {
                  newProps['scaleX'] = 1;
                }
              }
            }

            if (handle.N) {
              if (!this.snappingX) {
                const ratio = target.height / target.width;
                const newWidth = target.width * newProps['scaleX'];
                const newHeight = newWidth * ratio;
                const newScaleY = newHeight / target.height;

                const newTop = target.top + xFactor * (diff * ratio);
                this.snapKeepTop = newTop;
                newProps['top'] = newTop;

                newProps['scaleY'] = newScaleY;
              } else {
                newProps['scaleY'] = 1;
                newProps['top'] = this.snapKeepTop;
              }
            }

            target.set(newProps);
            target.setCoords();

            this.snappingX = true;
            snappedLoc = location.loc;
            adjusted = true;
          }
        }
      }
      if (!inSnapRange) {
        if (this.snappingX) {
          this.snapped = false;
        }
        this.snappingX = false;
      }
      if (adjusted) break;
    }
    return snappedLoc;
  }

  snapForHorizontalLine(
    locations,
    target,
    targetLocations,
    handle,
    borderBuffer,
  ) {
    let adjusted = false;
    let snappedLoc = undefined;
    let inSnapRange = false;

    for (let i = 0; i < targetLocations.length; ++i) {
      const targetLocation = targetLocations[i];

      for (let j = 0; j < locations.length; ++j) {
        const location = locations[j];
        if (target.pageItemVO.get('id') == location.pageItemID) continue;

        if (!adjusted) {
          const snapToNumber = location.loc;

          const diff = targetLocation - snapToNumber;

          if (Math.abs(diff) < location.tolerance) {
            target.setCoords();

            inSnapRange = true;
            let newProps = {};
            let xFactor = -1;

            if (handle.S) {
              xFactor = 1;
              if (!this.snapped) {
                newProps['scaleY'] =
                  target.scaleY * (target.height / (target.height + diff));
              } else {
                newProps['scaleY'] = 1;
              }
            }

            if (handle.N) {
              xFactor = -1;
              const twistedTopDiff =
                target.top - target.getBoundingRect(true, true).top;
              let twistedAdjustment = 0;
              if (twistedTopDiff > 1) {
                twistedAdjustment = twistedTopDiff;
              }
              newProps['top'] = snapToNumber + borderBuffer + twistedAdjustment;
              if (!this.snappingY) {
                const newScaleY = target.scaleY + diff / target.height;
                newProps['scaleY'] = newScaleY;
              } else {
                newProps['scaleY'] = 1;
              }
            }

            if (handle.E) {
              if (!this.snapped) {
                // const newScaleX = (1+ (-xFactor * diff / target.width)) / target.scaleX;
                // newProps['scaleX'] = newScaleX;

                const ratio = target.height / target.width;
                const newHeight = target.height * newProps['scaleY'];
                const newWidth = newHeight / ratio;
                const newScaleX = newWidth / target.width;
                newProps['scaleX'] = newScaleX;
              } else {
                newProps['scaleX'] = 1;
              }
            }

            if (handle.W) {
              if (!this.snappingY) {
                const ratio = target.height / target.width;
                const newHeight = target.height * newProps['scaleY'];
                const newWidth = newHeight / ratio;
                const newScaleX = newWidth / target.width;
                newProps['scaleX'] = newScaleX;

                const newLeft = target.left + xFactor * (diff / ratio);
                newProps['left'] = newLeft;
                this.snapKeepLeft = newLeft;
              } else {
                newProps['scaleX'] = 1;
                newProps['left'] = this.snapKeepLeft;
              }
            }

            target.set(newProps);
            this.snappingY = true;
            snappedLoc = location.loc;
            adjusted = true;
          }
        }
      }
      if (!inSnapRange) {
        if (this.snappingY) {
          this.snapped = false;
        }
        this.snappingY = false;
      }
      if (adjusted) break;
    }
    return snappedLoc;
  }

  snapForAdjustedWidth(
    locations,
    target,
    targetRect,
    handle,
    borderBuffer = 0,
  ) {
    let adjusted = false;
    let snappedLoc = undefined;
    let inSnapRange = false;
    let targetCoordinate = handle.W
      ? targetRect.x
      : targetRect.x + targetRect.width;

    for (let j = 0; j < locations.length; ++j) {
      const location = locations[j];
      if (target.pageItemVO.get('id') == location.pageItemID) continue;

      if (!adjusted) {
        const snapToNumber = location.loc;

        const diff = targetCoordinate - snapToNumber;

        if (Math.abs(diff) < location.tolerance) {
          target.setCoords();

          inSnapRange = true;
          let newProps = {};
          if (handle.E) {
            //only adjust width
            snappedLoc = snapToNumber;
            newProps['width'] = target.width - diff;
            target.set(newProps);
            break;
          } else {
            let targetRightCoord =
              (target.pageItemVO.get('positionX') +
                target.pageItemVO.get('width')) *
              this.dim.ratioWidth;

            //need to adjust the X, then the width

            snappedLoc = snapToNumber;
            newProps['left'] = snappedLoc;
            newProps['width'] = targetRightCoord - snapToNumber;
            target.set(newProps);
            break;
          }
        }
      }
    }
    return snappedLoc;
  }

  snapForAdjustedHeight(
    locations,
    target,
    targetRect,
    handle,
    borderBuffer = 0,
  ) {
    let adjusted = false;
    let snappedLoc = undefined;
    let inSnapRange = false;
    let targetCoordinate = handle.N
      ? targetRect.y
      : targetRect.y + targetRect.height;

    for (let j = 0; j < locations.length; ++j) {
      const location = locations[j];
      if (target.pageItemVO.get('id') == location.pageItemID) continue;

      if (!adjusted) {
        const snapToNumber = location.loc;

        const diff = targetCoordinate - snapToNumber;

        if (Math.abs(diff) < location.tolerance) {
          target.setCoords();

          inSnapRange = true;
          let newProps = {};
          if (handle.S) {
            //only adjust width
            snappedLoc = snapToNumber;
            newProps['height'] = target.height - diff;
            target.set(newProps);
          } else {
            //need to adjust the X, then the width
            let targetBottomCoord =
              (target.pageItemVO.get('positionY') +
                target.pageItemVO.get('height')) *
              this.dim.ratioWidth;

            snappedLoc = snapToNumber;
            newProps['top'] = snappedLoc;
            newProps['height'] = targetBottomCoord - snapToNumber;
            target.set(newProps);
          }
        }
      }
    }
    return snappedLoc;
  }

  drawHorizontalSnapLine(y, offset) {
    this.setState({ showHorizontalSnap: true, snapY: y, snapOffSetY: offset });
  }

  drawVerticalSnapLine(x, offset) {
    this.setState({ showVerticalSnap: true, snapX: x, snapOffSetX: offset });
  }

  checkSafetyZone(target) {
    if (this.isHardCover() || this.hasNoSafetyZone()) {
      return;
    }

    const productInfo = this.props.productInfo;
    let pageSafetyX = this.props.selectedSize
      ? this.props.selectedSize.get('safetyX')
      : productInfo.get('pageSafetyX');
    let pageSafetyY = this.props.selectedSize
      ? this.props.selectedSize.get('safetyY')
      : productInfo.get('pageSafetyY');

    let outerRectHeight = this.dim.ratio - pageSafetyY * 2;
    if (
      this.props.project.get('type') === PicabooConstants.PRODUCT_TYPES.TOTE
    ) {
      pageSafetyY = pageSafetyY * this.dim.ratio;
      outerRectHeight = productInfo.get('pageSafetyHeight') * this.dim.ratio;
    }

    //we multiply dimensions by the scale to support richText items
    const innerRect = getContainingRectangle({
      x: target.left / this.dim.width,
      y: target.top / this.dim.width,
      width: (target.width * target.scaleX) / this.dim.width,
      height: (target.height * target.scaleY) / this.dim.width,
      rotation: target.angle,
    });

    const outerRect = {
      x: pageSafetyX,
      y: pageSafetyY,
      width: 1 - pageSafetyX * 2,
      height: outerRectHeight,
    };

    clearTimeout(this.safetyTimeout);

    if (contains(outerRect, innerRect)) {
      if (this.state.showSafety) {
        this.setState({ showSafety: false });
      }
    } else {
      if (!this.state.showSafety) {
        this.setState({ showSafety: true });
      }
      this.safetyTimeout = setTimeout(
        function () {
          this.setState({ showSafety: false });
        }.bind(this),
        3000,
      );
    }
  }

  swapBackground = (options) => {
    let { dispatch, pageItems, images, selectedPageItems } = this.props;

    let gi2 = selectedPageItems.last();
    let image2 = images.get(gi2.get('imageID'));
    let object2 = this.renderer.getCanvasItem(gi2.get('id'));

    let graphicItemId = this.getBackgroundGraphicItemId();
    let pi1 = null;
    if (graphicItemId) {
      let gi1 = pageItems.get(graphicItemId);
      let image1 = images.get(gi1.get('imageID'));
      // let object1 = options.target;

      pi1 = this.changeImage(image1, object2, gi2);
    }

    let newBackgroundGraphicItem = this.setBackgroundImage(image2, false);

    this.props.dispatch(
      setBackgroundImage(
        [this.getPage()],
        newBackgroundGraphicItem,
        image2,
        image2.get('imageID'),
        pi1,
      ),
    );

    dispatch(enableSwapPhotoPicker(false));
    dispatch(renderCanvasFromStore());
  };

  swapSelected = (options) => {
    this.resetHoverCursor();

    let { dispatch, pageItems, images, selectedPageItems } = this.props;
    let page1ID = '';
    let page2ID = '';

    if (this.isGraphicsItemWithNormalImageSelected(options)) {
      logEvent('Edit photo', 'Swap photo', 'Completed', 0);

      let selectedItems = this.getSelectedItems();

      //NOTE: The images we are swapping may be from separate pages

      let gi1 = pageItems.get(selectedItems[0]);
      let image1 = images.get(gi1.get('imageID'));
      let object1 = options.target;

      let gi2 = selectedPageItems.last();
      let image2 = images.get(gi2.get('imageID'));
      let object2 = this.renderer.getCanvasItem(gi2.get('id'));

      delete GraphicItemUtils.imageCache[gi2.get('id')];
      delete GraphicItemUtils.imageCache[gi1.get('id')];

      if (gi1.get('id') != gi2.get('id')) {
        let pi1 = this.changeImage(image1, object2, gi2);
        let pi2 = this.changeImage(image2, object1, gi1);
        page1ID = pi1.get('pageID');
        page2ID = pi2.get('pageID');

        dispatch(updatePageItems([pi1, pi2]));
      }
    }

    dispatch(enableSwapPhotoPicker(false));

    //Re-rendering the canvas needs to happen AFTER disabling the swap photo mode
    if (page1ID != page2ID) {
      dispatch(renderCanvasFromStore());
    }
  };

  changeImage(projectImage, obj, pi) {
    let newPI;
    if (projectImage) {
      // const fit = GraphicItemUtils.fitImage(projectImage.get('width'), projectImage.get('height'), obj.width, obj.height);
      let wellWidth = pi.get('width') * this.dim.ratioWidth;
      let wellHeight = pi.get('height') * this.dim.ratioWidth;
      const fit = GraphicItemUtils.fitImage(
        projectImage.get('width'),
        projectImage.get('height'),
        wellWidth,
        wellHeight,
        projectImage,
      );
      const imgWidth = fit.width / this.dim.ratioWidth;
      const imgHeight = fit.height / this.dim.ratioWidth;

      newPI = pi.withMutations((list) => {
        list
          .set('imageID', projectImage.get('imageID'))
          .set('image_width', imgWidth)
          .set('image_height', imgHeight)
          .set(
            'image_positionX',
            ((fit.width - wellWidth) / 2 / this.dim.ratioWidth) * -1,
          )
          .set(
            'image_positionY',
            ((fit.height - wellHeight) / 2 / this.dim.ratioWidth) * -1,
          );
      });

      if (obj) {
        this.renderer._drawPageItem(newPI, () => {
          this.renderer.selectMulti(newPI.get('id'));
        });
      }
    } else {
      newPI = pi.withMutations((list) => {
        list.set('imageID', null);
      });

      if (obj) {
        this.renderer._drawPageItem(newPI, () => {
          this.renderer.selectMulti(newPI.get('id'));
        });
      }
    }

    return newPI;
  }

  resetHoverCursor() {
    if (this.hoverObject) {
      this.hoverObject.hoverCursor = null;
      this.hoverObject = null;
    }
  }

  swapMouseOver(e) {
    this.resetHoverCursor();
    if (this.isGraphicsItemWithNormalImageSelected(e)) {
      this.hoverObject = e.target;
      e.target.hoverCursor = 'pointer';
    }
  }

  isGraphicsItemWithNormalImageSelected(e) {
    if (!e.target) {
      return false;
    }

    const { pageItems } = this.props;
    const selectedItems = this.getSelectedItems();

    return (
      selectedItems.length == 1 &&
      pageItems.get(selectedItems[0]).get('itemType') ==
        PicabooConstants.ITEM_TYPES.GRAPHICITEM &&
      pageItems.get(selectedItems[0]).get('type') ==
        PicabooConstants.WELL_TYPES.NORMAL
    );
  }

  swapMouseOut(e) {
    this.resetHoverCursor();
  }

  setupCanvasEvents(props) {
    let canvas = this.canvas;

    if (this.state.isPanning) {
      //No op....
    } else if (
      props.selection.eyeDropperEnabled === true ||
      props.selection.shapeSpotterEnabled === true ||
      props.selection.swapPhotoEnabled === true ||
      props.selection.backgroundPlacerEnabled === true ||
      props.selection.layoutPlacerEnabled ||
      props.selection.designPlacerEnabled
    ) {
      let canvasElement = this.getElement('c');
      let ctx = canvasElement.getContext('2d');
      let eyePicker = document.getElementById('eyeDropperFloater');
      let eyePickerIcon = document.getElementById('eyeDropperIcon');
      let eyePickerHexColor = document.getElementById('eyeDropperHexColor');
      let eyeDropperText = document.getElementById('eyeDropperText');
      eyeDropperText.innerText = 'Hover over page';
      eyePicker.style.left = `0%`;
      eyePicker.style.top = `0%`;
      eyePickerIcon.style.backgroundColor = 'unset';
      eyePickerIcon.style.display = 'none';
      eyePickerHexColor.innerText = '';

      canvas.off(); // reset all canvas events

      if (props.selection.swapPhotoEnabled) {
        canvas.on('object:selected', this.swapSelected);
        // canvas.on('mouse:down', this.swap);
        canvas.on('mouse:down', this.swapBackground);
        this.makeInteractive();
        canvas.on('mouse:over', this.swapMouseOver.bind(this));
        canvas.on('mouse:out', this.swapMouseOut.bind(this));

        canvas.defaultCursor = 'pointer';
        //canvas.hoverCursor = 'pointer';
      } else {
        this.makeNonInteractive();
      }

      if (props.selection.eyeDropperEnabled === true) {
        canvas.hoverCursor = 'crosshair';
        canvas.defaultCursor = 'crosshair';
        eyePicker.style.display = 'flex';
        canvas.on(
          'mouse:move',
          function (e) {
            eyeDropperText.innerText = 'Click to select';
            let mouse = canvas.getPointer(e.e);
            // let xOffset = parseInt(e.e.clientX);
            // let yOffset = parseInt(e.e.clientY);
            let pixelRatio = window.devicePixelRatio || 1;
            let x = parseInt(mouse.x);
            let y = parseInt(mouse.y);
            let px = ctx.getImageData(
              x * pixelRatio,
              y * pixelRatio,
              1,
              1,
            ).data;
            let hexcolor = '#' + toHex(px[0]) + toHex(px[1]) + toHex(px[2]);
            let eyeConX = mouse.x;
            let eyeConY = mouse.y;

            if (x < 0 || x > this.state.containerWidth) {
              return;
            }

            if (this.props.uid == '1') {
              eyeConX += this.state.containerWidth;
            } else if (!this.props.twoPage) {
              eyeConX =
                (this.state.containerWidth - this.state.pageWidth) / 2 +
                mouse.x;
            }

            if (x > -1 && x <= canvas.width && y > -1 && y < canvas.height) {
              eyePicker.style.left = `${eyeConX}px`;
              eyePicker.style.top = `${eyeConY}px`;
              eyePickerIcon.style.backgroundColor = hexcolor;
              eyePickerIcon.style.display = '';
              eyePickerHexColor.innerText = hexcolor;
              this.setState({
                selectedEyeDropperColor: hexcolor,
                validEyeDropper: true,
              });
            } else {
              this.setState({ validEyeDropper: false });
            }
          }.bind(this),
        );
      }
      if (
        props.selection.shapeSpotterEnabled === true ||
        props.selection.backgroundPlacerEnabled === true ||
        props.selection.layoutPlacerEnabled ||
        props.selection.designPlacerEnabled
      ) {
        canvas.defaultCursor = 'pointer';
        canvas.hoverCursor = 'pointer';
      }

      canvas.on(
        'mouse:down',
        function (e) {
          if (props.selection.backgroundPlacerEnabled === true) {
            if (this.getPage().get('canSetBackground')) {
              let img = this.props.selection.backgroundImage;
              if (img) {
                if (img === 'ALL') {
                  let pageID = this.getPage().get('id');
                  let sectionID = this.props.section.get('sectionID');
                  this.props.dispatch(setBackgroundAllPages(pageID, sectionID));
                } else {
                  const backgroundImage = new BackgroundToProjectImageAdapter(
                    img,
                  );
                  this.props.dispatch(
                    convertBackground(fromJS(backgroundImage.toObject())),
                  );
                  this.props.dispatch(addProjectImage(backgroundImage));
                  this.dispatchSelection([]); //de-selects any other page items and selects the current page
                  this.props.dispatch(
                    setBackgroundImage(
                      [this.getPage()],
                      new BackgroundImageToPageItemAdapter(
                        img,
                        this.getPage(),
                        this.dim,
                      ),
                      backgroundImage,
                      img.imageID,
                    ),
                  );
                }
              } else {
                let thePage = this.getPage();
                let thePages = new List([thePage]);
                this.props.dispatch(
                  setBackgroundColor(
                    thePages,
                    this.props.selection.eyeDropperColor,
                  ),
                );
                let theUpdatedPages = PageUtils.setPageBackgroundColor(
                  thePages,
                  this.props.selection.eyeDropperColor,
                );
                this.props.dispatch(updatePages(theUpdatedPages.toJS()));
              }
              this.props.dispatch(renderCanvasFromStore());
            }
            this.props.dispatch(enableBackgroundPlacer(false));
          }
          if (props.selection.layoutPlacerEnabled === true) {
            let ids = [];
            if (this.getPage().get('canChangeLayout')) {
              if (props.selection.layoutCallback) {
                this.props.dispatch(enableLayoutPlacer(false));
                props.selection.layoutCallback(
                  props.selection.layouts,
                  undefined,
                  false,
                  this.getPage().get('id'),
                );
              } else {
                const pageIDList = [this.getPage().get('id')];
                //if the 'layout' has a pageID, then its not a real layout, but just a page.
                if (
                  props.selection.layouts.length === 1 &&
                  props.selection.layouts[0].pageID
                ) {
                  this.props.dispatch(
                    applyPageLayoutToPages(
                      props.selection.layouts[0],
                      pageIDList,
                    ),
                  );
                } else {
                  const layout = props.selection.layouts[0];
                  this.props.dispatch(applyLayoutToPages(layout, pageIDList));
                  logEvent(
                    'Layout applied',
                    this.props.project.get('type') +
                      ' > ' +
                      this.props.project.get('subtype'),
                    layout.id,
                    parseInt(layout.wells),
                  );
                }
              }
              this.props.dispatch(enableLayoutPlacer(false));
            }
          }
          if (props.selection.designPlacerEnabled) {
            let targetPage = this.getPage();
            if (targetPage.get('canChangeLayout')) {
              const targetIsBlank =
                targetPage.get('pageItems').count() === 0 &&
                !targetPage.get('background').has('graphicItem');
              const isSpread =
                targetPage.get('pageType') ===
                  PicabooConstants.PAGE_TYPES.NORMAL &&
                targetPage.get('ratioBase') > 1;

              if (targetIsBlank) {
                this.props.dispatch(
                  applyPageDesignToPages(
                    props.selection.design.pageID,
                    props.selection.design.srcProjectPK,
                    targetPage.get('id'),
                    !!props.selection.design.isThemePage,
                  ),
                );
              } else {
                this.props.dispatch(
                  showConfirmationModal(
                    (doImport) => {
                      if (doImport) {
                        this.props.dispatch(
                          applyPageDesignToPages(
                            props.selection.design.pageID,
                            props.selection.design.srcProjectPK,
                            targetPage.get('id'),
                            !!props.selection.design.isThemePage,
                          ),
                        );
                      }
                    },
                    'Import Design',
                    `Are you sure you want to replace the contents of this ${isSpread ? 'spread' : 'page'}?`,
                  ),
                );
              }
            }
            this.props.dispatch(enableDesignPlacer(false));
          }

          if (props.selection.eyeDropperEnabled === true) {
            eyePicker.style.display = 'none';

            if (this.state.validEyeDropper) {
              props.selection.eyeDropperCallback &&
                props.selection.eyeDropperCallback(
                  this.state.selectedEyeDropperColor,
                );
              this.props.dispatch(renderCanvasFromStore());
              this.props.dispatch(enableEyeDropper(false));
            }
          }
          if (props.selection.shapeSpotterEnabled === true) {
            let mouse = canvas.getPointer(e.e);
            let x = parseInt(mouse.x);
            let y = parseInt(mouse.y);
            let xCenter = x / this.dim.ratioWidth;
            let yCenter = y / this.dim.ratioWidth;

            let newPageItem = null;

            switch (props.selection.shapeName) {
              case 'line':
                if (!this.getPage().get('canAddImage')) {
                  return;
                }
                logEvent('Toolbar', 'Added line', '', 0);
                newPageItem = createLineItem(
                  this.getPage().get('id'),
                  xCenter,
                  yCenter,
                );
                break;
              case 'text':
                if (!this.getPage().get('canAddCaption')) {
                  return;
                }
                logEvent('Toolbar', 'Add text', 'Complete', 0);
                newPageItem = createTextItem(
                  this.getPage().get('id'),
                  xCenter,
                  yCenter,
                );
                break;
              case 'placeholder':
                if (!this.getPage().get('canAddImage')) {
                  return;
                }
                logEvent('Toolbar', 'Add placeholder', 'Complete', 0);
                newPageItem = createGraphicItem(
                  this.getPage().get('id'),
                  '',
                  0.5,
                  0.5,
                  PicabooConstants.IMAGE_TYPES.NORMAL,
                  xCenter,
                  yCenter,
                );
                break;
              case 'richText':
                if (!this.getPage().get('canAddCaption')) {
                  return;
                }
                logEvent('Toolbar', 'Add text', 'Complete', 0);
                newPageItem = createRichTextItem(
                  this.getPage().get('id'),
                  xCenter,
                  yCenter,
                  this.props.settings,
                );
                break;
              case 'photo':
                if (!this.getPage().get('canAddImage')) {
                  return;
                }
                newPageItem = createGraphicItem(
                  this.getPage().get('id'),
                  '',
                  0.25,
                  0.25,
                  PicabooConstants.IMAGE_TYPES.NORMAL,
                  xCenter,
                  yCenter,
                );
                newPageItem = newPageItem.set('adjustGraphicItemOnAddImage');
                break;
              default:
                if (!this.getPage().get('canAddImage')) {
                  return;
                }
                logEvent(
                  'Toolbar',
                  'Added shape',
                  props.selection.shapeName,
                  0,
                );
                newPageItem = createShapeItem(
                  this.getPage().get('id'),
                  props.selection.shapeName,
                  xCenter,
                  yCenter,
                );
            }

            if (newPageItem !== null) {
              this.props.dispatch(
                addPageItems(this.getPage().get('id'), newPageItem),
              );
              this.props.dispatch(renderCanvasFromStore());
              this.dispatchSelection([newPageItem.get('id')]);
              canvas.on(
                'mouse:up',
                function (e) {
                  this.props.dispatch(enableShapePlacer(false));
                }.bind(this),
              );
            } else {
              this.props.dispatch(enableShapePlacer(false));
            }
          }
        }.bind(this),
      );
    } else {
      canvas.off(); // reset all canvas events
      this.makeInteractive();

      canvas.hoverCursor = 'move';
      canvas.defaultCursor = 'default';

      canvas.on('object:added', this.objectAdded);
      canvas.on('object:removed', this.objectRemoved);
      // canvas.on('before:selection:cleared', this.beforeSelectionCleared);
      canvas.on('object:modified', this.objectModified);
      canvas.on('object:scaling', this.objectScaling);
      canvas.on('object:moving', this.objectMoving);
      canvas.on('object:rotating', this.objectRotating);
      canvas.on('selection:created', this.selectionChanged);
      canvas.on('selection:updated', this.selectionChanged);

      canvas.on('mouse:over', function (e) {
        if (
          e.target &&
          e.target.type === 'group' &&
          e.target._objects.length > 0
        ) {
          e.target._objects.map((o) => {
            if (
              (o.picabooType !== 'warning' && o.type === 'i-text') ||
              o.type === 'circle'
            ) {
              o.set('visible', true);
            }
          });
          canvas.renderAll();
        }
      });

      canvas.on('mouse:out', function (e) {
        if (
          e.target &&
          e.target.type === 'group' &&
          e.target._objects.length > 0
        ) {
          e.target._objects.map((o) => {
            if (
              o.picabooType !== 'warning' &&
              (o.type === 'i-text' || o.type === 'circle')
            ) {
              o.set('visible', false);
            }
          });
          canvas.renderAll();
        }
      });

      canvas.on('mouse:move', (e) => {
        if (
          e.subTargets &&
          e.subTargets[0] &&
          (e.subTargets[0].type === 'circle' ||
            e.subTargets[0].picabooType == 'warning' ||
            e.subTargets[0].picabooType === 'hoverMenuCircle')
        ) {
          canvas.defaultCursor = 'pointer';
          canvas.hoverCursor = 'pointer';
        } else {
          canvas.hoverCursor = 'move';
          canvas.defaultCursor = 'default';
        }
        canvas.renderAll();
      });

      canvas.on(
        'mouse:down',
        function (e) {
          if (
            e.target &&
            e.subTargets &&
            e.subTargets[0] &&
            e.subTargets[0].picabooType == 'warning'
          ) {
            this.props.dispatch(setSelectedTab(TAB_WARNINGS));
            this.props.dispatch(setSidePanelOpened(true));
          }
          if (
            e.target &&
            e.subTargets &&
            e.subTargets[0] &&
            e.subTargets[0].picabooType == PANNING
          ) {
            const startX = e.pointer.x;
            const startY = e.pointer.y;

            this.setState({ isPanning: true });
            const pageItem = e.target.pageItemVO;

            let cachedImage =
              GraphicItemUtils.imageCache[pageItem.get('imageID')];
            let group = e.target;

            let outline = new fabric.Rect({
              id: 'crop-rect',
              left: group.left,
              top: group.top,
              angle: group.angle,
              width: group.width,
              height: group.height,
              stroke: 'rgb(42, 67, 101)',
              strokeWidth: 1,
              strokeDashArray: [5, 5],
              fill: 'rgba(255, 255, 255, 1)',
              lockRotation: true,
              selectable: false,
            });

            let boundingRect = group.getBoundingRect();

            let outlineRotate = new fabric.Rect({
              id: 'crop-rect',
              left: group.left,
              top: group.top,
              angle: group.angle,
              width: boundingRect.width,
              height: boundingRect.height,
              stroke: 'rgb(42, 67, 101)',
              strokeWidth: 1,
              strokeDashArray: [5, 5],
              fill: 'rgba(255, 255, 255, 1)',
              lockRotation: true,
              selectable: false,
            });

            canvas.off();
            canvas.add(outline);

            let scaleX = pageItem.get('image_width') / pageItem.get('width');
            fabric.Image.fromObject(cachedImage, (i) => {
              i.opacity = 0.2;
              let scale = (group.width / i.width) * scaleX;
              i.scale(scale);
              group.width = i.getScaledWidth();
              group.height = i.getScaledHeight();
              i.top = -group.height / 2.0;
              i.left = -group.width / 2.0;

              let p = pageItemPositionToFabric(pageItem, group, outline);
              group.left = p.left;
              group.top = p.top;

              const firstLeft = group.left;
              const firstTop = group.top;

              group.forEachObject((item) => {
                group.remove(item);
              });
              group.add(i);
              canvas.renderAll();

              let overLapX = i.getScaledWidth() - outline.width;
              let overLapY = i.getScaledHeight() - outline.height;

              outlineRotate.width = overLapX;
              outlineRotate.height = overLapY;
              const radians = (outlineRotate.angle * Math.PI) / 180.0;
              outlineRotate.left =
                outlineRotate.left -
                overLapX * Math.cos(radians) +
                overLapY * Math.sin(radians);
              outlineRotate.top =
                outlineRotate.top -
                overLapX * Math.sin(radians) -
                overLapY * Math.cos(radians);
              outlineRotate.setCoords();
              let outlineRotateCoords = outlineRotate.getCoords();

              let lines = [];
              lines.push(
                new Segment(
                  new Point(outlineRotateCoords[0].x, outlineRotateCoords[0].y),
                  new Point(outlineRotateCoords[1].x, outlineRotateCoords[1].y),
                ),
              );
              lines.push(
                new Segment(
                  new Point(outlineRotateCoords[1].x, outlineRotateCoords[1].y),
                  new Point(outlineRotateCoords[2].x, outlineRotateCoords[2].y),
                ),
              );
              lines.push(
                new Segment(
                  new Point(outlineRotateCoords[2].x, outlineRotateCoords[2].y),
                  new Point(outlineRotateCoords[3].x, outlineRotateCoords[3].y),
                ),
              );
              lines.push(
                new Segment(
                  new Point(outlineRotateCoords[3].x, outlineRotateCoords[3].y),
                  new Point(outlineRotateCoords[0].x, outlineRotateCoords[0].y),
                ),
              );

              handlePanning(
                group,
                firstLeft,
                firstTop,
                startX,
                startY,
                e.pointer.x,
                e.pointer.y,
                outlineRotate,
                lines,
              );

              canvas.bringToFront(group);
              canvas.renderAll();

              group.on('moving', function (e) {
                const pointerX = e.pointer.x;
                const pointerY = e.pointer.y;

                handlePanning(
                  group,
                  firstLeft,
                  firstTop,
                  startX,
                  startY,
                  pointerX,
                  pointerY,
                  outlineRotate,
                  lines,
                );
              });

              canvas.on('mouse:up', () => {
                let { x, y } = fabricPositionToPageItemPosition(
                  pageItem,
                  group,
                  outline,
                );

                let updatedPI = group.pageItemVO
                  .set('image_positionX', x)
                  .set('image_positionY', y);
                this.props.dispatch(updatePageItems([updatedPI]));

                canvas.clear();
                this.props.dispatch(renderCanvasFromStore());
                this.setState({ isPanning: false });
              });
            });
          }

          this.panningStart = e.pointer;

          if (props.selection.placerEnabled) {
            props.dispatch(activatePlacer(false));
          }

          if (
            e.target == null &&
            e.subTargets.length == 0 &&
            this.canvas.getActiveObjects().length == 0 &&
            !this.canvas.getActiveObject() &&
            this.getPage().get('pageType') !==
              PicabooConstants.PAGE_TYPES.ENVELOPE
          ) {
            this.selectBackgroundImage();
            e.e.stopImmediatePropagation();
            e.e.preventDefault();
          } else {
            if (isMobile()) {
              if (e.target.pageItemVO.get('imageID')) {
                this.props.dispatch(showPositionModal(true));
                this.props.dispatch(setSidePanelOpened(true));
              } else {
                this.props.dispatch(toggleImageManagement());
                this.props.dispatch(togglePhotosModal());
              }
            } else {
              let itemType = e.target.pageItemVO.get('itemType');
              if (itemType == PicabooConstants.ITEM_TYPES.RICH_TEXT) {
                this.props.dispatch(setSelectedTab(TAB_EDIT_RICH_TEXT));
                if (!this.props.sidePanelOpened)
                  this.props.dispatch(setSidePanelOpened(true));
              }
            }
          }
        }.bind(this),
      );
      canvas.on('mouse:up', (e) => {
        if (
          e.subTargets &&
          e.subTargets[0] &&
          e.subTargets[0].picabooType === 'hoverMenuCircle'
        ) {
          this.setState({
            showWellMenu: !this.state.showWellMenu,
            wellMenuCoords: { x: e.e.x, y: e.e.y },
          });
        }
        this.setState({ showHorizontalSnap: false });
        this.setState({ showVerticalSnap: false });
      });
      canvas.on('mouse:dblclick', (e) => {
        if (
          e.target !== undefined &&
          e.target !== null &&
          e.target.pageItemVO
        ) {
          let itemType = e.target.pageItemVO.get('itemType');
          if (itemType == PicabooConstants.ITEM_TYPES.CAPTION) {
            this.props.dispatch(captionEditing(true));
          } else if (itemType == PicabooConstants.ITEM_TYPES.GRAPHICITEM) {
            this.props.dispatch(showPositionModal(true));
            this.props.dispatch(setSelectedTab(TAB_EDIT_PHOTO));
            this.props.dispatch(setSidePanelOpened(true));
          }
        } else {
          //they probably double clicked on the background
          let background = this.getBackgroundGraphicItemId();
          if (background !== null) {
            this.props.dispatch(showPositionModal(true));
          }
        }
      });

      if (
        props.selectedPageItems.size == 1 &&
        // don't set active object if selected item is background since it is not a fabric image
        this.getBackgroundGraphicItemId() !=
          props.selectedPageItems.last().get('id') &&
        this.renderer
      ) {
        const item = this.renderer.getCanvasItem(
          props.selectedPageItems.last().get('id'),
        );
        // Selected item might not be on this canvas if we're in a 2 page editor
        if (item) {
          canvas.setActiveObject(item);
          if (
            props.selection.eyeDropperEnabled !==
              this.props.selection.eyeDropperEnabled &&
            item.pageItemVO.get('itemType') ==
              PicabooConstants.ITEM_TYPES.RICH_TEXT
          ) {
            let selection = this.props.selection;
            item.selectionStart = selection.dropperTextSelection.startIndex;
            item.selectionEnd = selection.dropperTextSelection.endIndex;
            item.enterEditing();
          }
        }
      }
    }

    // NOTE: The drop event handler must be setup here, rather than as an attribute in the render() function,
    // otherwise the 'layerX' and 'layerY' properties are not set for some reason....
    let canvasContainer = this.getElement('canvasDiv');

    if (canvasContainer && !canvasContainer.eventsAdded) {
      canvasContainer.addEventListener(
        'drop',
        this.handleDrop.bind(this),
        false,
      );
      canvasContainer.eventsAdded = true;
    }
  }

  handleTextSelectionChange = (selectedStyles, start, end) => {
    this.props.dispatch(textSelected(selectedStyles, start, end));
  };

  handleKeyDown = (e) => {
    if (isMobile()) {
      return;
    }

    if (e.keyCode === KEY.LEFT) {
      this.moveSelectedItems(-1, 0);
    } else if (e.keyCode === KEY.UP) {
      this.moveSelectedItems(0, -1);
    } else if (e.keyCode === KEY.RIGHT) {
      this.moveSelectedItems(1, 0);
    } else if (e.keyCode === KEY.DOWN) {
      this.moveSelectedItems(0, 1);
    }
  };

  handleDragOver = (e) => {
    if (e.preventDefault) {
      e.preventDefault(); // Necessary. Allows us to drop.
    }
    if (e.stopPropagation) {
      e.stopPropagation();
    }
    let canvas = this.canvas;

    const isIE = !!document.documentMode;
    if (!isIE) {
      if (
        this.getPage().get('pageType') === PicabooConstants.PAGE_TYPES.INDEX &&
        !this.props.project.get('canEditIndex')
      ) {
        e.dataTransfer.dropEffect = 'none';
      } else if (e.nativeEvent.target === canvas.upperCanvasEl) {
        e.dataTransfer.dropEffect = 'copy';
      } else {
        e.dataTransfer.dropEffect = 'none';
      }
    }

    if (
      this.props.selection.draggedImageType !==
      PicabooConstants.IMAGE_TYPES.STICKER
    ) {
      let obj = this.findTargetObject(e);

      if (obj) {
        if (obj == canvas.getActiveObject()) {
          return;
        }

        canvas.getObjects().forEach((canvasObj) => {
          let canvasPI = canvasObj.pageItemVO;
          if (
            canvasObj !== obj &&
            canvasPI &&
            canvasPI.get('itemType') == PicabooConstants.ITEM_TYPES.GRAPHICITEM
          ) {
            this.returnHighLightToNormal(canvasObj);
            canvas.requestRenderAll();
          }
        });

        let pi = obj.pageItemVO;
        if (pi) {
          if (
            pi.get('itemType') == PicabooConstants.ITEM_TYPES.GRAPHICITEM &&
            pi.get('type') == PicabooConstants.WELL_TYPES.NORMAL
          ) {
            this.highlightFabricObject(obj);
            canvas.requestRenderAll();
            return false;
          }
        }
      } else {
        canvas.getObjects().forEach((canvasObj) => {
          let pi = canvasObj.pageItemVO;
          if (
            pi &&
            pi.get('itemType') == PicabooConstants.ITEM_TYPES.GRAPHICITEM
          ) {
            this.returnHighLightToNormal(canvasObj);
            canvas.requestRenderAll();
          }
        });
      }
    }

    if (canvas.getActiveObject()) {
      //Remove selection
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    }
    return false;
  };

  highlightFabricObject(obj) {
    if (!fabric.filterBackend) {
      fabric.filterBackend = fabric.initFilterBackend();
    }
    let childObjects = obj.getObjects().filter((innerObj) => {
      return innerObj.hasOwnProperty('filters') && innerObj.filters.length == 0;
    });
    childObjects.forEach((innerObj, index) => {
      let brightnessFilter = new fabric.Image.filters.Brightness({
        brightness: 0.2,
      });
      innerObj.filters = [brightnessFilter];
      innerObj.applyFilters();
    });
    //now try empty wells
    let emptyWellImage = obj.getObjects().filter((innerObj) => {
      return (
        innerObj.hasOwnProperty('picabooType') &&
        innerObj.picabooType === 'emptyWellImage'
      );
    });
    if (emptyWellImage.length > 0) {
      let fill = emptyWellImage[0].fill;
      fill.source = PageRendererEmptyWellHelper.getHighlightImage();
      emptyWellImage[0].set('fill', fill);
    }
  }

  returnHighLightToNormal(obj) {
    let childObjects = obj.getObjects().filter((innerObj) => {
      return innerObj.hasOwnProperty('filters') && innerObj.filters.length > 0;
    });

    childObjects.forEach((innerObj, index) => {
      innerObj.filters = [];
      innerObj.applyFilters();
    });
    //now try empty wells
    let emptyWellImage = obj.getObjects().filter((innerObj) => {
      return (
        innerObj.hasOwnProperty('picabooType') &&
        innerObj.picabooType === 'emptyWellImage'
      );
    });
    if (emptyWellImage.length > 0) {
      let fill = emptyWellImage[0].fill;
      fill.source = PageRendererEmptyWellHelper.getEmptyWellImage();
      emptyWellImage[0].set('fill', fill);
    }
  }

  handleDrop(e) {
    e.preventDefault();
    if (e.stopPropagation) {
      e.stopPropagation(); // stops the browser from redirecting.
    }

    //first, remove all of the highlights
    this.canvas.getObjects().forEach((canvasObj) => {
      let canvasPI = canvasObj.pageItemVO;
      if (
        canvasObj !== obj &&
        canvasPI &&
        canvasPI.get('itemType') == PicabooConstants.ITEM_TYPES.GRAPHICITEM
      ) {
        this.returnHighLightToNormal(canvasObj);
      }
    });

    if (
      this.getPage().get('pageType') === PicabooConstants.PAGE_TYPES.INDEX &&
      !this.props.project.get('canEditIndex')
    ) {
      return;
    }

    if (this.props.selectedPages.size === 0) {
      this.props.dispatch(selectPages([this.getPage().get('id')]));
    }

    let canvas = this.canvas;
    // Get applyLayout data to apply dragged layout
    let data = e.dataTransfer.getData('applyLayout');
    if (data) {
      const layout = JSON.parse(data);
      const ids = [this.getPage().get('id')];

      if (layout.numPages === this.props.pages.size) {
        this.props.dispatch(
          convertSpreadPages(null, () => {
            this.props.pages.forEach((p) =>
              this.props.dispatch(applyLayoutToPages(layout, [p.get('id')])),
            );
          }),
        );
        return;
      }

      this.props.dispatch(applyLayoutToPages(layout, ids));
      logEvent(
        'Layout applied',
        this.props.project.get('type') +
          ' > ' +
          this.props.project.get('subtype'),
        layout.id,
        parseInt(layout.wells),
      );
      return;
    }

    data = e.dataTransfer.getData('text');
    if (!data) {
      return;
    }
    let values = data.split(':');

    let imageID = values[0]; // e.dataTransfer.getData('text').split(':');
    let imageType = '';
    if (values.length == 2) {
      imageType = values[1]; // = e.dataTransfer.getData('imageType');
    }
    let projectImage = this.getProjectImage(imageID, imageType);

    let obj = this.findTargetObject(e);
    let pi = obj ? obj.pageItemVO : null;
    let isGraphicItem =
      obj &&
      pi &&
      pi.get('itemType') == PicabooConstants.ITEM_TYPES.GRAPHICITEM;
    if (isGraphicItem && !pi.get('imageID')) {
      //Dropped on empty well, so just add it
      this.addDroppedImageToWell(e, projectImage);
      return;
    }

    //If we get to this point the user has dragged an image from the media strip and dropped it either on an existing
    //graphic item or somewhere else on the page.

    const canReplace =
      isGraphicItem &&
      (pi.get('type') === PicabooConstants.WELL_TYPES.NORMAL ||
        pi.get('type') === PicabooConstants.WELL_TYPES.FIXED_ELEMENT);
    if (
      !canReplace &&
      !this.getPage().get('canAddImage') &&
      !this.getPage().get('canSetBackground')
    ) {
      return;
    }

    const canSetBackground =
      this.getPage().get('canSetBackground') &&
      this.props.project.get('type') !==
        PicabooConstants.PRODUCT_TYPES.YEARBOOK_AD;

    if (!canReplace) {
      if (this.getPage().get('canAddImage')) {
        this.addDroppedImageToPage(e, projectImage);
      }
      return;
    }

    this.addDroppedImageToWell(e, projectImage);
  }

  findTargetObject(e) {
    let allObjects = this.canvas.getObjects();
    let pointer = this.canvas.getPointer(e, true);

    let selectedObject = null;

    allObjects.forEach((object) => {
      let type = object.pageItemVO.get('type');
      if (
        type == PicabooConstants.WELL_TYPES.NORMAL ||
        type == PicabooConstants.WELL_TYPES.INTERACTIVE_ELEMENT ||
        type == PicabooConstants.WELL_TYPES.FIXED_ELEMENT
      ) {
        let isCollision = object.containsPoint({ x: pointer.x, y: pointer.y });
        if (isCollision) {
          selectedObject = object;
        }
      }
    });

    return selectedObject;
  }

  addDroppedImageToPage = (e = null, projectImage = null) => {
    if (!e) {
      e = this.state.dropEvent;
      projectImage = this.state.droppedImage;
    }

    if (!projectImage) {
      return;
    }

    let imageID = projectImage.get('imageID');
    let imageType = projectImage.get('imageType');
    let x = e.layerX || 0,
      y = e.layerY || 0;

    let newGI = this.createGraphicItem(imageID, x, y, imageType);
    logEvent('Photos', 'Added to page', 'Single', 0);
    this.renderer.updateImages(this.props.images);
    this.renderer._drawPageItem(newGI);
  };

  addDroppedImageToWell = (e = null, projectImage = null) => {
    logEvent('Photos', 'Added to page', 'Replace', 0);
    if (!e || !projectImage) {
      e = this.state.dropEvent;
      projectImage = this.state.droppedImage;
    }

    if (!projectImage) {
      return;
    }

    let dim = this.dim;
    let imageID = projectImage.get('imageID');

    let obj = this.findTargetObject(e);

    if (obj && obj.get('type') === 'group') {
      let pi = obj.pageItemVO;
      if (
        pi &&
        pi.get('itemType') === PicabooConstants.ITEM_TYPES.GRAPHICITEM
      ) {
        let fit = GraphicItemUtils.fitImage(
          projectImage.get('width'),
          projectImage.get('height'),
          obj.width,
          obj.height,
          projectImage,
        );
        let imgWidth = fit.width / dim.ratioWidth;
        let imgHeight = fit.height / dim.ratioWidth;

        // MB-807, MB-806 Always re-set image_rotation to 0 (not rotated) when dropping new image
        let newPI = pi.withMutations((list) => {
          list
            .set('imageID', imageID)
            .set('image_width', imgWidth)
            .set('image_height', imgHeight)
            .set('image_rotation', 0)
            .set(
              'image_positionX',
              ((fit.width - obj.width) / 2 / dim.ratioWidth) * -1,
            )
            .set(
              'image_positionY',
              ((fit.height - obj.height) / 2 / dim.ratioWidth) * -1,
            );
        });

        this.props.dispatch(updatePageItems([newPI]));
        this.renderer.updateImages(this.props.images);
        this.renderer._drawPageItem(newPI, () => {
          this.props.dispatch(updatePageItems([newPI])); // Update resolution
          this.renderer.selectMulti(newPI.get('id'));
        });
      }
    }
  };

  addDroppedImageAsBackground = () => {
    logEvent('Photos', 'Added to page', 'Background', 0);
    this.setBackgroundImage(this.state.droppedImage);
  };

  addDroppedImageAsSpreadBackground = () => {
    logEvent('Photos', 'Added to page', 'Spread Background', 0);
    this.props.dispatch(convertSpreadPages(this.state.droppedImage));
  };

  setBackgroundImage(projectImage, dispatch = true) {
    let dim = this.dim;
    // let gi = new BackgroundImageToPageItemAdapter({imageID:projectImage.get('imageID')}, this.getPage());
    let gi = new BackgroundImageToPageItemAdapter(
      projectImage.toJS(),
      this.getPage(),
      dim,
    );
    // const ratioBase = this.getPage().get('ratioBase');
    //
    // let fit = GraphicItemUtils.fitImage(projectImage.get('width'), projectImage.get('height'), dim.width, dim.height);
    // let imgWidth = fit.width / dim.width * ratioBase;
    // let imgHeight = fit.height / dim.width * ratioBase;
    //
    // gi.image_width = imgWidth;
    // gi.image_height = imgHeight;
    // gi.image_positionX = (((fit.width - dim.width)/2) / dim.width) * -1 * ratioBase;
    // gi.image_positionY = (((fit.height - dim.height)/2) / dim.width) * -1 * ratioBase;

    if (dispatch) {
      this.props.dispatch(
        setBackgroundImage(
          [this.getPage()],
          gi,
          projectImage.toJS(),
          projectImage.get('imageID'),
        ),
      );

      this.props.dispatch(renderCanvasFromStore());
    }

    let immutableGI = fromJS({ ...gi });
    this.renderer.redrawBackground(immutableGI, () => {
      this.props.dispatch(selectPageItems([immutableGI.get('id')]));
    });

    return gi;
  }

  moveSelectedItems = (dx, dy) => {
    let obj = this.canvas.getActiveObject();
    if (obj) {
      if (
        obj.pageItemVO &&
        (obj.pageItemVO.has('portraitID') ||
          obj.pageItemVO.get('type') ===
            PicabooConstants.WELL_TYPES.FIXED_ELEMENT)
      ) {
        return;
      }

      obj.left += dx;
      obj.top += dy;
      obj.setCoords();
      this.canvas.renderAll();

      clearTimeout(this.moveTimeoutHandle);
      this.moveTimeoutHandle = setTimeout(() => {
        this.targetModified(obj);
      }, 600);
    }
  };

  getSelectedItems() {
    const activeObjects = this.canvas.getActiveObjects();

    let active = [];
    let items = activeObjects.map((object) => {
      if (object.pageItemVO) {
        active.push(object);
        return object.pageItemVO.get('id');
      }
    });

    if (isMobile() && active.length > 1) {
      //Only allow one object to be selected at a time
      this.canvas.setActiveObject(active[0]);
      items = [items[0]];
    }

    return items;
  }

  resizeGraphicItem(obj, maintainAspectRatio = true) {
    //Graphic items consists of an Image and optionally a corner Overlay image and a Rect (for border) inside a Group

    //When a group is resized, the only thing that changes is the scale of the Group itself.
    //So, we manually resize the group dimensions according to the scale, and then size the inner rect/image accordingly.

    //      --------------------------------
    //      | @     corner layer           |
    //      |   |----------------------|   |
    //      |   |    border layer      |   |
    //      |   |   |--------------|   |   |
    //      |   |   |   IMG        |   |   |

    const originalHeight = obj.height;
    const originalWidth = obj.width;
    let resized = this.resizeScaledObject(obj); // this is the max size (cornerOverLay or border)
    const rescaleX = originalWidth / obj.width;
    const rescaleY = originalHeight / obj.height;
    const scaledHeight = obj.getScaledHeight();
    const scaledWidth = obj.getScaledWidth();
    let pi = obj.pageItemVO;
    let projectImage = this.getProjectImage(
      pi.get('imageID'),
      pi.get('imageType'),
    );
    //let dim = this.calcPageDimensions();
    let dim = this.dim;
    let maskShape = pi.get('maskShape');
    if (maskShape == 'null') {
      maskShape = null;
    }
    let children = obj.getObjects();
    let actualBorderThickness = this.getActualBorderFromItem(pi);
    let borderWidth = resized.width + actualBorderThickness * 2;
    let borderHeight = resized.height + actualBorderThickness * 2;
    for (const child of children) {
      let type = child.get('type');

      if (child.picabooType == 'emptyWellImage') {
        child.set('width', scaledWidth);
        child.set('height', scaledHeight);
      } else if (child.picabooType == 'EmptyWellGroup') {
        child.set('width', child.width / rescaleX);
        child.set('height', child.height / rescaleY);
        child.set('left', child.left / rescaleX);

        let emptyWellGroupChildren = child.getObjects();
        for (const emptyChild of emptyWellGroupChildren) {
          if (emptyChild.picabooType == 'EmptyWellText') {
            if (resized.width < 80 || resized.height < 80) {
              // hide text when well is small
              emptyChild.set('visible', false);
            } else {
              emptyChild.set('visible', true);
              const min = Math.min(emptyChild.fontSize / rescaleX, 28); // not too big, not too small
              emptyChild.set('fontSize', Math.max(min, 8));
            }
          }
        }
      } else if (child.picabooType == 'hoverMenuCircle') {
        child.set('left', resized.width / 2 + child.adjust.left);
        child.set('top', -resized.height / 2 + child.adjust.top);
      } else if (child.picabooType == 'warning') {
        child.set('left', -resized.width / 2 + child.adjust.left);
        child.set('top', -resized.height / 2 + child.adjust.top);
      } else if (type == 'rect') {
        child.set({
          width: borderWidth,
          height: borderHeight,
          strokeWidth: actualBorderThickness,
        });

        if (maskShape) {
          child.clipTo = (ctx) => {
            GraphicItemMasks.applyMask(
              ctx,
              maskShape,
              borderWidth,
              borderHeight,
            );
          };
        }
      } else if (type == 'image') {
        if (
          pi.get('imageID') == undefined ||
          pi.get('imageID') == null ||
          pi.get('imageID') == ''
        ) {
          return;
        }

        let imageDimensions = GraphicItemUtils.adjustImageToWell(
          pi,
          dim,
          obj,
          maintainAspectRatio,
        );

        let htmlImage = GraphicItemUtils.imageCache[pi.get('imageID')];

        let mirrorHorizontal = GraphicItemUtils.shouldApplyFilter(pi, 'Mirror');
        let mirrorVertical = GraphicItemUtils.shouldApplyFilter(
          pi,
          'VerticalMirror',
        );

        this.postProcess(
          htmlImage,
          projectImage,
          imageDimensions,
          obj,
          child,
          resized,
          maskShape,
          pi.get('image_rotation'),
          mirrorHorizontal,
          mirrorVertical,
        );
      }
    }
  }

  postProcess(
    htmlImage,
    projectImage,
    imageDimensions,
    obj,
    child,
    resized,
    maskShape,
    rotation,
    horizontalMirror,
    verticalMirror,
  ) {
    //const dim = this.calcPageDimensions();
    const dim = this.dim;

    let rotated = rotation == 90 || rotation == 270;
    const piDim = {
      x: imageDimensions.x / dim.ratioWidth,
      y: imageDimensions.y / dim.ratioWidth,
      width: rotated
        ? imageDimensions.height / dim.ratioWidth
        : imageDimensions.width / dim.ratioWidth,
      height: rotated
        ? imageDimensions.width / dim.ratioWidth
        : imageDimensions.height / dim.ratioWidth,
    };

    this.isLowRes = GraphicItemUtils.isLowRes(
      piDim,
      projectImage,
      this.props.productInfo.get('resolutionFactor'),
    );
    let resizedImage = GraphicItemUtils.resizeImage(
      htmlImage,
      imageDimensions.width * window.devicePixelRatio,
      imageDimensions.height * window.devicePixelRatio,
      imageDimensions.x,
      imageDimensions.y,
      obj.width * window.devicePixelRatio,
      obj.height * window.devicePixelRatio,
      projectImage,
      rotation,
      horizontalMirror,
      verticalMirror,
    );
    //See if we need to swap width/height to respect EXIF data
    let pi = child.pageItemVO;
    let imageOrientation =
      projectImage.get('orientation') || pi.get('image_orientation');

    if (
      (imageOrientation == PicabooConstants.IMAGE_ORIENTATION.LEFT ||
        imageOrientation == PicabooConstants.IMAGE_ORIENTATION.RIGHT) &&
      resized.width > resized.height
    ) {
      child.set({
        width: resized.height,
        height: resized.width,
      });
    } else {
      child.set({
        width: resized.width,
        height: resized.height,
      });
    }

    child.setElement(resizedImage);

    if (maskShape) {
      child.clipTo = (ctx) => {
        GraphicItemMasks.applyMask(
          ctx,
          maskShape,
          resized.width,
          resized.height,
        );
      };
    }

    // Add or remove warning icon based on resolution of resized image
    let isWarningSet = false;
    obj.getObjects().forEach((child) => {
      if (child.picabooType === 'warning') {
        if (!this.isLowRes) {
          obj.remove(child);
        } else {
          isWarningSet = true;
        }
      }
    });

    if (!isWarningSet && this.isLowRes) {
      const warningIcon = GraphicItemUtils.getWarningIcon(
        resized.width,
        resized.height,
      );
      const warningCircle = GraphicItemUtils.getWarningCircle(
        resized.width,
        resized.height,
      );
      obj.add(warningCircle);
      obj.add(warningIcon);
    }
  }

  resizeScaledObject(obj, resizingFromCorner = false) {
    //http://jsfiddle.net/davidtorroija/nawLjtn8/ this helps
    let options = { scaleX: 1, scaleY: 1 };
    let pi = obj.pageItemVO;

    let dim = this.dim;

    let borderSize = 0;
    let strokeWidth = 0;
    let scaledStroke = 0;
    let scaledStrokeY = 0;

    if (obj.type != 'group') {
      borderSize = pi.get('borderEnabled') ? pi.get('borderSize') : 0;
      strokeWidth = 0.01 * borderSize * dim.ratioWidth;
      scaledStroke = strokeWidth * obj.scaleX;
      scaledStrokeY = strokeWidth * obj.scaleY;
    }

    if (strokeWidth) {
      options.strokeWidth = strokeWidth;
    }

    switch (obj.type) {
      case 'circle':
        options.radius =
          obj.radius * obj.scaleX + (scaledStroke - strokeWidth) / 2;
        break;
      case 'ellipse':
        if (resizingFromCorner) {
          options.rx = obj.rx * obj.scaleX + (scaledStroke - strokeWidth) / 2;
          options.ry = obj.ry * obj.scaleY + (scaledStrokeY - strokeWidth) / 2;
        } else {
          options.rx = obj.width / 2;
          options.ry = obj.height / 2;
        }

        break;
      case 'line':
        //When we draw a line we subtract the line thickness from the width of the line

        strokeWidth = (pi.get('thickness') * dim.scale) / dim.ratioBase;
        options.strokeWidth = strokeWidth;
        scaledStroke = strokeWidth * obj.scaleX;
        options.width = obj.width * obj.scaleX + scaledStroke - strokeWidth;

        break;
      case 'rect':
        let scaledWidth = obj.width * obj.scaleX + scaledStroke - strokeWidth;
        let scaledHeight =
          obj.height * obj.scaleY + scaledStrokeY - strokeWidth;
        options.width = scaledWidth;
        options.height = scaledHeight;
        break;

      case 'textbox':
        //the scale does not change when you resize a textbox so there is nothing to change here
        options = { scaleX: obj.scaleX, scaleY: obj.scaleY };
        break;
      default:
        options.width = Math.max(obj.width * obj.scaleX, 1);
        options.height = Math.max(obj.height * obj.scaleY, 1);
        break;
    }

    obj.set(options);
    return options;
  }

  /**
   *
   * @param pi PageItemVO
   */
  getActualBorderFromItem(pi) {
    if (pi.has('filters')) {
      for (const filter of pi.get('filters')) {
        if (
          filter.get('name') == 'Border' &&
          (filter.has('enabled') ? filter.get('enabled') : true)
        ) {
          let values = filter.get('value').split('|'); // color|width
          //The 'width' is not always specified (see defect 11685)
          let borderThickness = 1;
          if (values.length > 1) {
            borderThickness = values[1];
          }

          return this.renderer.calcActualBorderThickness(
            borderThickness,
            this.renderer._ratioWidth,
          );
        }
      }
    }
    return 0;
  }

  /**
   * Determine the width, height, and scale based on our available space and the ratio.
   */
  calcPageDimensions() {
    //ratio = height/width
    //height = ratio*width
    //width = height/ratio

    let printAdjustForPageNumber = 0;
    if (
      isMobile() &&
      this.props.project.get('type') === PicabooConstants.PRODUCT_TYPES.PRINTS
    ) {
      printAdjustForPageNumber = 24;
    }

    // Container dimensions
    let cwidth = 0;
    let editorContainer = document.getElementById('editorContainer');
    if (this.props.reviewMode) {
      editorContainer = document.getElementById('reviewContainer'); //Get review container for review mode
    }
    let cheight = editorContainer.clientHeight - 15 - printAdjustForPageNumber;

    if (this.props.twoPage) {
      if (this.props.showStacked) {
        cheight = cheight / 2;
        cwidth = editorContainer.clientWidth - 10;
      } else cwidth = editorContainer.clientWidth / 2;
    } else {
      cwidth = editorContainer.clientWidth - 10;
      //cwidth = this.refs.canvasDiv.clientWidth;
    }

    if (this.isFrame()) {
      cwidth *= 0.9;
      cheight *= 0.9;
    } else if (this.isCanvas()) {
      cwidth *= 0.95;
      cheight *= 0.95;
    } else {
      cwidth -= 10;
    }

    let page = this.getPage();

    let ratioBase = page.get('ratioBase') || 1.0;
    let ratio = page.get('ratio') / ratioBase;

    let d = {
      width: 0,
      height: 0,
      marginX: 0,
      marginY: 0,
      widthWithMargin: 0,
      heightWithMargin: 0,
      scale: 1,
      uswidth: PicabooConstants.PAGE.UNSCALED_PAGE_WIDTH,
      ratio: ratio,
      ratioBase: ratioBase,
      ratioWidth: 0,
    };

    if (ratio >= 1.0 || this.isCard()) {
      //Portrait orientation
      d.heightWithMargin = d.height = cheight;
      d.widthWithMargin = d.width = cheight / ratio;
    } else {
      //Landscape orientation
      d.widthWithMargin = d.width = cwidth;
      d.heightWithMargin = d.height = ratio * cwidth;
    }

    //Reduce size to fit if necessary
    if (d.height > cheight) {
      d.heightWithMargin = d.height = cheight;
      d.widthWithMargin = d.width = d.height / ratio;
    } else if (d.width > cwidth) {
      d.widthWithMargin = d.width = cwidth;
      d.heightWithMargin = d.height = d.width * ratio;
    }

    const isCard =
      this.props.project.get('type') === PicabooConstants.PRODUCT_TYPES.CARD;
    d.ratioWidth = d.width / this.getPage().get('ratioBase');

    d.scale =
      (d.ratioWidth > d.height || isCard ? d.ratioWidth : d.height) /
      PicabooConstants.PAGE.UNSCALED_PAGE_WIDTH;
    d.zoom = d.width / PicabooConstants.PAGE.UNSCALED_PAGE_WIDTH;

    //update the dimensions in the redux store
    this.props.dispatch(setEditorDim(d));
    this.drawSafetyZone();

    return d;
  }

  // drawLine(x, y, itemWidth, itemHeight, pi) {
  //     let canvas = this.canvas;
  //
  //     //The length of the line is the itemWidth, and represents the hypotenuse of a right triangle where
  //     //the x distance is the adjacent and the y distance is the opposite.
  //
  //     let adjacent = this.getAdjacent(pi.rotation, itemWidth);
  //     let opposite = this.getOpposite(pi.rotation, itemWidth);
  //
  //     let x2 = x + adjacent;
  //     let y2 = y + opposite;
  //
  //
  //     let line = new fabric.Line([x,y,x2,y2], {
  //         fill: this.decimalToRgba(pi.color),
  //         stroke: this.decimalToRgba(pi.color),
  //         strokeWidth: itemHeight,
  //         strokeLineCap: pi.caps
  //     });
  //
  //     line.pageItemVO;
  //     canvas.insertAt(line, pi.zindex);
  // }
  //
  // cosDegrees(angle) {return Math.cos(angle/180*Math.PI);}
  // sinDegrees(angle) {return Math.sin(angle/180*Math.PI);}
  // getAdjacent(angle, hypotenuse) { return this.cosDegrees(angle) * hypotenuse; }
  // getOpposite(angle, hypotenuse) { return this.sinDegrees(angle) * hypotenuse; }

  getPage() {
    return this.props.page;
  }

  getProjectImage(imageID, imageType) {
    if (!this.props.images) {
      return undefined;
    }

    let image = this.props.images.get(imageID);

    return image;
  }

  /**
   * Generate a default graphic item using the specified image.
   * @param imageID
   * @param x
   * @param y
   * @param imageType
   */
  createGraphicItem(imageID, x, y, imageType) {
    //ratio = height/width
    //height = ratio*width
    //width = height/ratio

    let dim = this.dim;
    let projectImage = this.getProjectImage(imageID, imageType);
    if (projectImage) {
      projectImage = projectImage.toJS();
    }

    const imageRatio = projectImage.height / projectImage.width;

    let width = 0.5;
    let height = imageRatio * width;

    // BWO-250: Images dragged directly onto the canvas result in the image being displayed half size on retina displays.
    // To fix this, we add a very small adjustment.
    const adjustment = 0.0000000000001;

    if (imageRatio > 1) {
      height = width;
      width = height / imageRatio;
      width += adjustment;
    } else {
      height += adjustment;
    }

    switch (parseInt(projectImage.orientation)) {
      case PicabooConstants.IMAGE_ORIENTATION.LEFT:
      case PicabooConstants.IMAGE_ORIENTATION.RIGHT:
        if (Number(projectImage.height) < Number(projectImage.width)) {
          [width, height] = [height, width];
        }
        break;
    }

    let gi = createGraphicItem(
      this.getPage().get('id'),
      imageID,
      width,
      height,
      imageType,
      x / dim.ratioWidth,
      y / dim.ratioWidth,
    );
    this.props.dispatch(addPageItems(this.getPage().get('id'), gi));

    return gi;
  }
}

function mapStateToProps(state) {
  let props = {};

  try {
    props = {
      project: Selectors.projectMetadataSelector(state),
      loadingProject: Selectors.projectLoading(state),
      pages: Selectors.pagesSelector(state),
      pageItems: Selectors.pageItemsSelector(state),
      reviewSection: Selectors.reviewSectionSelector(state),
      section: Selectors.sectionSelector(state),
      images: Selectors.projectImagesSelector(state),
      user: state.user,
      selection: state.selection,
      selectedPageItems: Selectors.selectedPageItems(state),
      selectedPages: Selectors.selectedPages(state),
      previewMode: Selectors.previewModeSelector(state),
      productInfo: Selectors.productInfo(state),
      settings: Selectors.settings(state),
      renderCanvas: state.navigation.renderCanvas,
      renderPageID: state.navigation.renderPage,
      fonts: state.fonts,
      bottomPanelOpened: state.navigation.bottomPanelOpened,
      sidePanelOpened: state.navigation.sidePanelOpened,
      editTextModalOpen: state.modals.get('editTextModalOpen'),
      showmodal: state.modals.get('showmodal'),
      viewport: state.navigation.viewport,
      isBook: Selectors.isBook(state),
      leftPageNumber: Selectors.leftPageNumber(state),
      wrap: Selectors.wrapSelector(state),
      canvasHeight: state.navigation.canvasHeight,
      snap: state.snap,
      selectedSize: Selectors.selectedSize(state),
      isDesigner: Selectors.isDesigner(state),
      showImageManagement: state.navigation.showImageManagement,
      projectWarnings: Selectors.projectMetadataSelector(state).get('warnings'),
    };
  } catch (err) {
    console.error('Error mapping state to props', err, state);
  }

  return props;
}

CanvasContainer.propTypes = {
  uid: PropTypes.string,
  twoPage: PropTypes.bool,
  page: PropTypes.object,
  showStacked: PropTypes.bool,
  reviewMode: PropTypes.bool,
  reviewProject: PropTypes.object,
};

export const withT = withTranslation()(CanvasContainer);
export default connect(mapStateToProps)(withT);
